import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useAuth } from '../module_roots/AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faEdit, faTrash, faSave, faSearch, faRefresh, faSpinner,
    faExclamationTriangle, faCheckCircle, faTimes, faChevronLeft,
    faChevronRight, faFilePdf, faCalendar, faUser, faStore, faPhone,
    faEnvelope, faMapMarkerAlt, faTag, faExchangeAlt, faChevronDown,
    faExpand, faCompress, faArrowsAlt, faMinus, faPlus, faFilter, faEye,
    faBox, faBarcode, faBuilding, faBalanceScale, faRupeeSign, faCalculator,
    faCalendarAlt, faWarehouse, faPercent, faBoxes, faLayerGroup, faCubes,
    faPills, faCoins, faGift, faMoneyBillWave, faClock, faExclamationCircle,
    faList, faTable, faThList, faFileAlt, faDownload, faPrint, faSkull
} from '@fortawesome/free-solid-svg-icons';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

// ============================================
// CONSTANTS
// ============================================
const API_BASE = 'https://ourzestapp.online/oz_backend';
const PRODUCTS_ENDPOINT = `${API_BASE}/pharmacy_module/pharmacy_products.php`;
const BUSINESS_INFO_ENDPOINT = `${API_BASE}/get_business_info.php`;

// ============================================
// SHARED TRANSLATOR
// ============================================
const createSharedTranslator = () => {
    let cache = new Map();

    const loadFromCache = () => {
        try {
            const cached = localStorage.getItem('ourzest_translations');
            if (cached) {
                const parsed = JSON.parse(cached);
                Object.entries(parsed).forEach(([key, value]) => {
                    cache.set(key, value);
                });
            }
        } catch (e) { }
    };

    const saveToCache = () => {
        try {
            const cacheObj = {};
            cache.forEach((value, key) => {
                cacheObj[key] = value;
            });
            localStorage.setItem('ourzest_translations', JSON.stringify(cacheObj));
        } catch (e) { }
    };

    const translateAPI = async (text, targetLang) => {
        if (targetLang === 'en') return text;

        const dict = {
            'ur': {
                'Expired Products List': 'میعاد ختم شدہ مصنوعات کی فہرست',
                'Products that have expired': 'وہ مصنوعات جن کی میعاد ختم ہو چکی ہے',
                'Product Name': 'مصنوعات کا نام',
                'Product Code': 'مصنوعات کا کوڈ',
                'Batch Number': 'بیچ نمبر',
                'Barcode': 'بارکوڈ',
                'Category': 'زمرہ',
                'Company': 'کمپنی',
                'Packing Type': 'پیکنگ کی قسم',
                'Sub Packing Type': 'ذیلی پیکنگ کی قسم',
                'Items Per Pack': 'فی پیک اشیاء',
                'Unit': 'یونٹ',
                'Retail Price': 'فروخت قیمت',
                'Total Quantity': 'کل مقدار',
                'Quantity Alert': 'کم اسٹاک الرٹ',
                'Expiry Date': 'میعاد ختم ہونے کی تاریخ',
                'Actions': 'اعمال',
                'Added By': 'شامل کرنے والا',
                'Search products...': 'مصنوعات تلاش کریں...',
                'Refresh': 'ریفریش کریں',
                'Export PDF': 'پی ڈی ایف ایکسپورٹ کریں',
                'Edit': 'ترمیم کریں',
                'Delete': 'حذف کریں',
                'Close': 'بند کریں',
                'Save Changes': 'تبدیلیاں محفوظ کریں',
                'Cancel': 'منسوخ کریں',
                'Success': 'کامیابی',
                'Error': 'خرابی',
                'Warning': 'انتباہ',
                'Showing': 'دکھا رہا ہے',
                'of': 'میں سے',
                'Previous': 'پچھلا',
                'Next': 'اگلا',
                'All': 'سب',
                'Product Details': 'مصنوعات کی تفصیلات',
                'Switch Shop': 'شاپ تبدیل کریں',
                'No shop selected': 'کوئی شاپ منتخب نہیں',
                'Please select a shop to view products': 'براہ کرم مصنوعات دیکھنے کے لیے شاپ منتخب کریں',
                'S.No': 'نمبر',
                'Products': 'مصنوعات',
                'Shop': 'شاپ',
                'View': 'دیکھیں',
                'Update Product': 'مصنوعات اپڈیٹ کریں',
                'Updating...': 'اپڈیٹ ہو رہا ہے...',
                'Product updated successfully': 'مصنوعات کامیابی سے اپڈیٹ ہو گئی',
                'Error updating product': 'مصنوعات اپڈیٹ کرنے میں خرابی',
                'Confirm Delete': 'حذف کرنے کی تصدیق',
                'Yes, Delete': 'ہاں، حذف کریں',
                'No, Cancel': 'نہیں، منسوخ کریں',
                'Portrait': 'عمودی',
                'Landscape': 'افقی',
                'A4': 'A4',
                'Letter': 'لیٹر',
                'Legal': 'لیگل',
                'A5': 'A5',
                'Product Report': 'مصنوعات کی رپورٹ',
                'Product Directory': 'مصنوعات کی ڈائریکٹری',
                'Total Products': 'کل مصنوعات',
                'Generated': 'تیار کردہ',
                'Report ID': 'رپورٹ آئی ڈی',
                'Generated by': 'تیار کردہ بذریعہ',
                'Shop Code': 'شاپ کوڈ',
                'FILTERS APPLIED': 'فلٹرز لاگو ہیں',
                'Search': 'تلاش کریں',
                'From': 'سے',
                'To': 'تک',
                'Page': 'صفحہ',
                'Date': 'تاریخ',
                'MRP': 'زیادہ سے زیادہ خوردہ قیمت',
                'Cost Price': 'لاگت قیمت',
                'Low Stock': 'کم اسٹاک',
                'In Stock': 'اسٹاک میں',
                'Out of Stock': 'اسٹاک ختم',
                'BQuantity': 'بلک مقدار',
                'Bonus Stock': 'بونس اسٹاک',
                'Trade Price': 'تجارتی قیمت',
                'Extra Discount': 'اضافی ڈسکاؤنٹ',
                'Effective Discount': 'مؤثر ڈسکاؤنٹ',
                'Per Pack': 'فی پیک',
                'Per Unit': 'فی یونٹ',
                'Per Tablet': 'فی گولی',
                'Profit Margin': 'منافع کا مارجن',
                'Stock Value': 'اسٹاک کی قیمت',
                'Expiring Soon': 'جلد میعاد ختم',
                'days': 'دن',
                'MRP per Pack Unit': 'MRP فی پیک یونٹ',
                'MRP per Base Unit': 'MRP فی بیس یونٹ',
                'Cost per Pack Unit': 'لاگت فی پیک یونٹ',
                'Cost per Base Unit': 'لاگت فی بیس یونٹ',
                'Sell per Item Unit': 'فروخت فی آئٹم یونٹ',
                'Sell per Base Unit': 'فروخت فی بیس یونٹ',
                'Bonus Buy': 'بونس خریدیں',
                'Bonus Free': 'بونس مفت',
                'Actual Stock': 'اصل اسٹاک',
                'Purchased Qty': 'خرید شدہ مقدار',
                'Pack Content': 'پیک مواد',
                'Expired': 'میعاد ختم',
                'Expired Products': 'میعاد ختم شدہ مصنوعات',
                'Total Expired': 'کل میعاد ختم شدہ',
                'Days Overdue': 'دن گزر چکے',
                'Expired On': 'میعاد ختم ہونے کی تاریخ'
            },
            'ar': {
                'Expired Products List': 'قائمة المنتجات منتهية الصلاحية',
                'Products that have expired': 'المنتجات التي انتهت صلاحيتها',
                'Product Name': 'اسم المنتج',
                'Product Code': 'رمز المنتج',
                'Batch Number': 'رقم الدفعة',
                'Barcode': 'الباركود',
                'Category': 'الفئة',
                'Company': 'الشركة',
                'Packing Type': 'نوع التغليف',
                'Sub Packing Type': 'نوع التغليف الفرعي',
                'Items Per Pack': 'القطع لكل عبوة',
                'Unit': 'الوحدة',
                'Retail Price': 'سعر التجزئة',
                'Total Quantity': 'الكمية الإجمالية',
                'Quantity Alert': 'تنبيه الكمية المنخفضة',
                'Expiry Date': 'تاريخ الانتهاء',
                'Actions': 'الإجراءات',
                'Added By': 'أضيف بواسطة',
                'Search products...': 'بحث عن منتجات...',
                'Refresh': 'تحديث',
                'Export PDF': 'تصدير PDF',
                'Edit': 'تعديل',
                'Delete': 'حذف',
                'Close': 'إغلاق',
                'Save Changes': 'حفظ التغييرات',
                'Cancel': 'إلغاء',
                'Success': 'نجاح',
                'Error': 'خطأ',
                'Warning': 'تحذير',
                'Showing': 'عرض',
                'of': 'من',
                'Previous': 'السابق',
                'Next': 'التالي',
                'All': 'الكل',
                'Product Details': 'تفاصيل المنتج',
                'Switch Shop': 'تبديل المتجر',
                'No shop selected': 'لم يتم اختيار متجر',
                'Please select a shop to view products': 'الرجاء اختيار متجر لعرض المنتجات',
                'S.No': 'رقم',
                'Products': 'المنتجات',
                'Shop': 'متجر',
                'View': 'عرض',
                'Update Product': 'تحديث المنتج',
                'Updating...': 'جاري التحديث...',
                'Product updated successfully': 'تم تحديث المنتج بنجاح',
                'Error updating product': 'خطأ في تحديث المنتج',
                'Confirm Delete': 'تأكيد الحذف',
                'Yes, Delete': 'نعم، حذف',
                'No, Cancel': 'لا، إلغاء',
                'Portrait': 'عمودي',
                'Landscape': 'أفقي',
                'A4': 'A4',
                'Letter': 'رسالة',
                'Legal': 'قانوني',
                'A5': 'A5',
                'Product Report': 'تقرير المنتجات',
                'Product Directory': 'دليل المنتجات',
                'Total Products': 'إجمالي المنتجات',
                'Generated': 'تم الإنشاء',
                'Report ID': 'معرف التقرير',
                'Generated by': 'تم الإنشاء بواسطة',
                'Shop Code': 'رمز المتجر',
                'FILTERS APPLIED': 'المرشحات المطبقة',
                'Search': 'بحث',
                'From': 'من',
                'To': 'إلى',
                'Page': 'صفحة',
                'Date': 'التاريخ',
                'MRP': 'السعر الأقصى',
                'Cost Price': 'سعر التكلفة',
                'Low Stock': 'مخزون منخفض',
                'In Stock': 'في المخزون',
                'Out of Stock': 'نفد المخزون',
                'BQuantity': 'الكمية الإجمالية',
                'Bonus Stock': 'المخزون الإضافي',
                'Trade Price': 'سعر التجارة',
                'Extra Discount': 'خصم إضافي',
                'Effective Discount': 'الخصم الفعال',
                'Per Pack': 'لكل عبوة',
                'Per Unit': 'لكل وحدة',
                'Per Tablet': 'لكل قرص',
                'Profit Margin': 'هامش الربح',
                'Stock Value': 'قيمة المخزون',
                'Expiring Soon': 'ينتهي قريباً',
                'days': 'أيام',
                'MRP per Pack Unit': 'MRP لكل وحدة عبوة',
                'MRP per Base Unit': 'MRP لكل وحدة أساسية',
                'Cost per Pack Unit': 'التكلفة لكل وحدة عبوة',
                'Cost per Base Unit': 'التكلفة لكل وحدة أساسية',
                'Sell per Item Unit': 'البيع لكل وحدة عنصر',
                'Sell per Base Unit': 'البيع لكل وحدة أساسية',
                'Bonus Buy': 'مكافأة الشراء',
                'Bonus Free': 'مكافأة مجانية',
                'Actual Stock': 'المخزون الفعلي',
                'Purchased Qty': 'الكمية المشتراة',
                'Pack Content': 'محتوى العبوة',
                'Expired': 'منتهي الصلاحية',
                'Expired Products': 'المنتجات منتهية الصلاحية',
                'Total Expired': 'إجمالي المنتهية صلاحيتها',
                'Days Overdue': 'أيام متأخرة',
                'Expired On': 'تاريخ الانتهاء'
            }
        };

        if (dict[targetLang] && dict[targetLang][text]) {
            return dict[targetLang][text];
        }

        try {
            const response = await fetch('https://translate.argosopentech.com/translate', {
                method: 'POST',
                body: JSON.stringify({ q: text, source: 'en', target: targetLang }),
                headers: { 'Content-Type': 'application/json' }
            });
            if (response.ok) {
                const data = await response.json();
                return data.translatedText;
            }
        } catch (e) { }

        try {
            const response = await fetch(
                `https://api.mymemory.translated.net/get?q=${encodeURIComponent(text)}&langpair=en|${targetLang}`
            );
            if (response.ok) {
                const data = await response.json();
                return data.responseData.translatedText;
            }
        } catch (e) { }

        return text;
    };

    const translateText = async (text, targetLang) => {
        const cacheKey = `${text}_${targetLang}`;
        if (cache.has(cacheKey)) {
            return cache.get(cacheKey);
        }
        const translated = await translateAPI(text, targetLang);
        cache.set(cacheKey, translated);
        saveToCache();
        return translated;
    };

    const defaultTexts = {
        productList: 'Expired Products List',
        formDetails: 'Products that have expired',
        products: 'Expired Products',
        shopName: 'Shop',
        noShopSelected: 'No shop selected. Please select a shop first.',
        switchShop: 'Switch Shop',
        loadingShops: 'Loading shops...',
        noShops: 'No shops available',
        current: 'Current',
        searchPlaceholder: 'Search expired products...',
        refresh: 'Refresh',
        loadingUserData: 'Loading expired products...',
        selectShop: 'Please select a shop to view expired products',
        productName: 'Product Name',
        productCode: 'Product Code',
        batchNumber: 'Batch Number',
        barcode: 'Barcode',
        category: 'Category',
        company: 'Company',
        packingType: 'Packing Type',
        subPackingType: 'Sub Packing Type',
        itemsPerPack: 'Items Per Pack',
        unit: 'Unit',
        retailPrice: 'Retail Price',
        totalQuantity: 'Total Quantity',
        quantityAlert: 'Low Stock Alert',
        expiryDate: 'Expiry Date',
        actions: 'Actions',
        addedBy: 'Added By',
        serialNo: 'S.No',
        showing: 'Showing',
        of: 'of',
        previous: 'Previous',
        next: 'Next',
        all: 'All',
        productNameFilter: 'Product Name',
        productCodeFilter: 'Product Code',
        batchFilter: 'Batch Number',
        categoryFilter: 'Category',
        companyFilter: 'Company',
        dateRange: 'Date Range',
        addedByFilter: 'Added By',
        startDate: 'Start Date',
        endDate: 'End Date',
        exportToPDF: 'Export PDF',
        orientation: 'Orientation',
        portrait: 'Portrait',
        landscape: 'Landscape',
        pageSize: 'Page Size',
        a4: 'A4',
        letter: 'Letter',
        legal: 'Legal',
        a5: 'A5',
        productDetails: 'Product Details',
        productInfo: 'Product Information',
        pricingInfo: 'Pricing Information',
        stockInfo: 'Stock Information',
        editProduct: 'Edit Product',
        saveChanges: 'Save Changes',
        cancel: 'Cancel',
        confirmDelete: 'Confirm Delete',
        deleteMessage: 'Are you sure you want to delete this product?',
        deleteWarning: 'This action cannot be undone.',
        yesDelete: 'Yes, Delete',
        noCancel: 'No, Cancel',
        success: 'Success',
        error: 'Error',
        warning: 'Warning',
        close: 'Close',
        productReport: 'Expired Product Report',
        productDirectory: 'Expired Products Directory',
        totalProducts: 'Total Products',
        generated: 'Generated',
        reportId: 'Report ID',
        generatedBy: 'Generated by',
        shopCode: 'Shop Code',
        filtersApplied: 'FILTERS APPLIED',
        search: 'Search',
        from: 'From',
        to: 'To',
        page: 'Page',
        date: 'Date',
        mrp: 'MRP',
        costPrice: 'Cost Price',
        lowStock: 'Low Stock',
        inStock: 'In Stock',
        outOfStock: 'Out of Stock',
        bquantity: 'BQuantity',
        bonusStock: 'Bonus Stock',
        tradePrice: 'Trade Price',
        extraDiscount: 'Extra Discount',
        effectiveDiscount: 'Effective Discount',
        perPack: 'Per Pack',
        perUnit: 'Per Unit',
        perTablet: 'Per Tablet',
        profitMargin: 'Profit Margin',
        stockValue: 'Stock Value',
        expiringSoon: 'Expiring Soon',
        days: 'days',
        mrpPerPackUnit: 'MRP per Pack Unit',
        mrpPerBaseUnit: 'MRP per Base Unit',
        costPerPackUnit: 'Cost per Pack Unit',
        costPerBaseUnit: 'Cost per Base Unit',
        sellPerItemUnit: 'Sell per Item Unit',
        sellPerBaseUnit: 'Sell per Base Unit',
        bonusBuy: 'Bonus Buy',
        bonusFree: 'Bonus Free',
        actualStock: 'Actual Stock',
        purchasedQty: 'Purchased Qty',
        packContent: 'Pack Content',
        expired: 'Expired',
        expiredProducts: 'Expired Products',
        totalExpired: 'Total Expired',
        daysOverdue: 'Days Overdue',
        expiredOn: 'Expired On'
    };

    const getTranslations = async (lang) => {
        if (lang === 'en') {
            return defaultTexts;
        }

        const translations = {};
        const promises = [];

        Object.keys(defaultTexts).forEach(key => {
            const cacheKey = `${defaultTexts[key]}_${lang}`;
            if (cache.has(cacheKey)) {
                translations[key] = cache.get(cacheKey);
            } else {
                promises.push(
                    translateText(defaultTexts[key], lang).then(translated => {
                        translations[key] = translated;
                    })
                );
            }
        });

        if (promises.length > 0) {
            await Promise.all(promises);
            saveToCache();
        }

        return translations;
    };

    loadFromCache();

    return {
        getTranslations,
        translateText
    };
};

const expiredProductTranslator = createSharedTranslator();

// ============================================
// HELPER FUNCTIONS
// ============================================

const getUnitDisplayLabel = (unitOfMeasure) => {
    const labels = {
        '1_liter': 'Liter', '1.5_liter': 'Liter', '2_liter': 'Liter',
        '3_liter': 'Liter', '5_liter': 'Liter',
        '200_ml': 'Milliliter', '250_ml': 'Milliliter', '300_ml': 'Milliliter',
        '350_ml': 'Milliliter', '400_ml': 'Milliliter', '450_ml': 'Milliliter',
        '500_ml': 'Milliliter',
        '0.5_liter': 'Liter', '0.7_liter': 'Liter', '1_liter_alt': 'Liter',
        '2_liter_alt': 'Liter', '3_liter_alt': 'Liter', '4_liter': 'Liter',
        '5_liter_alt': 'Liter', '6_liter': 'Liter', '7_liter': 'Liter',
        '10_liter': 'Liter', '20_liter': 'Liter', '60_liter': 'Liter',
        '208_liter': 'Liter', 'milliliter': 'Milliliter', 'kilogram': 'Kilogram',
        'gram': 'Gram', 'piece': 'Piece', 'dozen': 'Dozen', 'box': 'Box',
        'packet': 'Packet', 'can': 'Can', 'bottle': 'Bottle',
        'tablet': 'Tablet', 'capsule': 'Capsule', 'strip': 'Strip',
        'vial': 'Vial', 'ampoule': 'Ampoule', 'sachet': 'Sachet', 'tube': 'Tube'
    };
    return labels[unitOfMeasure] || 'Unit';
};

const getPluralUnitLabel = (label) => {
    const plurals = {
        'Liter': 'Liters', 'Milliliter': 'Milliliters', 'Kilogram': 'Kilograms',
        'Gram': 'Grams', 'Piece': 'Pieces', 'Dozen': 'Dozens', 'Box': 'Boxes',
        'Packet': 'Packets', 'Can': 'Cans', 'Bottle': 'Bottles',
        'Tablet': 'Tablets', 'Capsule': 'Capsules', 'Strip': 'Strips',
        'Vial': 'Vials', 'Ampoule': 'Ampoules', 'Sachet': 'Sachets',
        'Tube': 'Tubes', 'Unit': 'Units'
    };
    return plurals[label] || label + 's';
};

const calculateBQuantity = (itemsPerPack, unitOfMeasure, totalQuantity) => {
    if (itemsPerPack <= 0 || totalQuantity <= 0 || !unitOfMeasure) return '';
    const unitValue = 1;
    const totalItems = itemsPerPack * totalQuantity;
    const bulkValue = totalItems * unitValue;
    const unitLabel = getUnitDisplayLabel(unitOfMeasure);

    let formattedValue;
    if (Math.floor(bulkValue) === bulkValue) {
        formattedValue = bulkValue.toFixed(0);
    } else {
        formattedValue = bulkValue.toFixed(2);
    }
    const finalLabel = bulkValue !== 1 ? getPluralUnitLabel(unitLabel) : unitLabel;
    return `${formattedValue} ${finalLabel}`;
};

const getCategoryLabel = (category) => {
    const map = {
        'medicine': 'Medicine', 'surgical': 'Surgical', 'baby_care': 'Baby Care',
        'personal_care': 'Personal Care', 'veterinary': 'Veterinary', 'herbal': 'Herbal',
        'supplement': 'Supplement', 'medical_equipment': 'Medical Equipment',
        'diagnostic': 'Diagnostic', 'first_aid': 'First Aid', 'others': 'Others'
    };
    return map[category] || category || '-';
};

const getStockStatus = (quantity, alert) => {
    if (quantity <= 0) return { label: 'Out of Stock', color: '#ef4444', bg: '#ef444420' };
    if (quantity <= alert) return { label: 'Low Stock', color: '#f59e0b', bg: '#f59e0b20' };
    return { label: 'In Stock', color: '#22c55e', bg: '#22c55e20' };
};

const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return dateString;
        return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    } catch { return dateString; }
};

// ============================================
// EXPIRY STATUS - RED FOR EXPIRED
// ============================================
const getExpiryStatus = (expiryDate) => {
    if (!expiryDate) return { label: 'No Expiry', color: '#6b7280', bg: '#6b728020', isExpired: false };
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const expiry = new Date(expiryDate);
    expiry.setHours(0, 0, 0, 0);
    const days = Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));
    
    // EXPIRED - RED BACKGROUND
    if (days < 0) { 
        return { 
            label: `Expired (${Math.abs(days)} days ago)`, 
            color: '#ffffff', 
            bg: '#dc2626',  // RED background
            isExpired: true,
            daysOverdue: Math.abs(days)
        };
    }
    if (days <= 30) return { label: `${days} days left`, color: '#f59e0b', bg: '#f59e0b20', isExpired: false };
    return { label: `${days} days left`, color: '#22c55e', bg: '#22c55e20', isExpired: false };
};

const toMoney = (value) => {
    const num = parseFloat(value);
    return Number.isFinite(num) ? `Rs. ${num.toFixed(2)}` : '-';
};

const toNumberOrDash = (value, decimals = 0) => {
    const num = parseFloat(value);
    return Number.isFinite(num) && num !== 0 ? num.toFixed(decimals) : (Number.isFinite(num) ? num.toFixed(decimals) : '-');
};

// ============================================
// TOAST NOTIFICATION
// ============================================
const ToastNotification = ({ toast, setToast, translations }) => {
    const getIconForType = (type) => {
        switch (type) {
            case 'success': return faCheckCircle;
            case 'error': return faExclamationTriangle;
            case 'warning': return faExclamationTriangle;
            default: return faCheckCircle;
        }
    };

    const getTitleForType = (type) => {
        switch (type) {
            case 'success': return translations.success || 'Success';
            case 'error': return translations.error || 'Error';
            case 'warning': return translations.warning || 'Warning';
            default: return '';
        }
    };

    if (!toast.show) return null;

    return (
        <div className={`ozlistui_toast-notification ${toast.type}`}>
            <div className="ozlistui_toast-content">
                <div className="ozlistui_toast-icon">
                    <FontAwesomeIcon icon={getIconForType(toast.type)} />
                </div>
                <div className="ozlistui_toast-text">
                    <div className="ozlistui_toast-title">{getTitleForType(toast.type)}</div>
                    <div className="ozlistui_toast-message">{toast.message}</div>
                </div>
                <button className="ozlistui_toast-close" onClick={() => setToast(prev => ({ ...prev, show: false }))}>
                    <FontAwesomeIcon icon={faTimes} />
                </button>
            </div>
        </div>
    );
};

// ============================================
// SHOP SELECTOR MODAL
// ============================================
const ShopSelectorModal = ({ isOpen, onClose, shops, currentShop, onSelectShop, translations, loading }) => {
    if (!isOpen) return null;

    return (
        <div className="ozlistui-modal-overlay" onClick={onClose}>
            <div className="ozlistui-modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="ozlistui-modal-header">
                    <h3>{translations.switchShop || 'Switch Shop'}</h3>
                    <button className="ozlistui-close-btn" onClick={onClose}>
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </div>
                <div className="ozlistui-modal-body">
                    {loading ? (
                        <div className="ozlistui-loading-shops">
                            <div className="ozlistui-loading-spinner-small"></div>
                            <p>{translations.loadingShops || 'Loading shops...'}</p>
                        </div>
                    ) : shops.length === 0 ? (
                        <div className="ozlistui-no-shops">
                            <p>{translations.noShops || 'No shops available'}</p>
                        </div>
                    ) : (
                        <div className="ozlistui-shop-list">
                            {shops.map((shop) => (
                                <div
                                    key={shop.shop_id}
                                    className={`ozlistui-shop-item ${currentShop?.shop_id === shop.shop_id ? 'ozlistui-active' : ''}`}
                                    onClick={() => onSelectShop(shop)}
                                >
                                    <div className="ozlistui-shop-item-icon">
                                        <FontAwesomeIcon icon={faStore} />
                                    </div>
                                    <div className="ozlistui-shop-item-details">
                                        <div className="ozlistui-shop-item-name">{shop.shop_name}</div>
                                        <div className="ozlistui-shop-item-code">{shop.shop_code || `ID: ${shop.shop_id}`}</div>
                                    </div>
                                    {currentShop?.shop_id === shop.shop_id && (
                                        <div className="ozlistui-shop-item-current">
                                            <FontAwesomeIcon icon={faCheckCircle} />
                                            <span>{translations.current || 'Current'}</span>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

// ============================================
// CUSTOM DROPDOWN COMPONENT
// ============================================
const CustomDropdown = ({ label, icon, value, onChange, options, placeholder, translations, disabled = false }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const wrapperRef = useRef(null);

    const selectedOption = options.find(opt => opt.value === value);
    const displayValue = selectedOption?.label || '';

    useEffect(() => {
        setSearchTerm(displayValue);
    }, [displayValue]);

    const filteredOptions = searchTerm.trim() === ''
        ? options
        : options.filter(opt =>
            opt.label.toLowerCase().includes(searchTerm.toLowerCase())
        );

    const handleSelect = (option) => {
        onChange(option.value);
        setSearchTerm(option.label);
        setIsOpen(false);
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    return (
        <div className="ozlistui-custom-dropdown-wrapper" ref={wrapperRef}>
            <div className="ozlistui-custom-input-wrapper">
                <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => {
                        setSearchTerm(e.target.value);
                        setIsOpen(true);
                        if (!options.find(opt => opt.label.toLowerCase() === e.target.value.toLowerCase())) {
                            onChange('');
                        }
                    }}
                    onFocus={() => setIsOpen(true)}
                    placeholder=" "
                    disabled={disabled}
                    autoComplete="off"
                />
                <label>
                    {icon && <FontAwesomeIcon icon={icon} />}
                    {label}
                </label>
                <FontAwesomeIcon icon={faChevronDown} className="ozlistui-custom-dropdown-arrow" />
            </div>

            {isOpen && (
                <div className="ozlistui-custom-dropdown-menu">
                    {filteredOptions.length > 0 ? (
                        filteredOptions.map(opt => (
                            <div
                                key={opt.value}
                                className={`ozlistui-custom-dropdown-option ${value === opt.value ? 'ozlistui-selected' : ''}`}
                                onClick={() => handleSelect(opt)}
                            >
                                {opt.label}
                            </div>
                        ))
                    ) : (
                        <div className="ozlistui-custom-dropdown-empty">
                            No options found
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

// ============================================
// VIEW DETAIL ROW
// ============================================
const DetailRow = ({ label, value }) => (
    <div className="ozlistui-view-row">
        <span className="ozlistui-view-label">{label}:</span>
        <span className="ozlistui-view-value">{value === '' || value === null || value === undefined ? '-' : value}</span>
    </div>
);

// ============================================
// MAIN EXPIRED PRODUCT LIST COMPONENT
// ============================================
const PharmacyExpiredProducts = () => {
    const { user, currentShop, switchShop, getShopsForSwitch } = useAuth();
    const [allProducts, setAllProducts] = useState([]);
    const [expiredProducts, setExpiredProducts] = useState([]);
    const [filteredProducts, setFilteredProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [pdfLoading, setPdfLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [showViewModal, setShowViewModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [productToDelete, setProductToDelete] = useState(null);
    const [editLoading, setEditLoading] = useState(false);
    const [showFilters, setShowFilters] = useState(false);
    const [fetchError, setFetchError] = useState('');

    // PDF Options
    const [pdfOrientation, setPdfOrientation] = useState('landscape');
    const [pdfPageSize, setPdfPageSize] = useState('a4');
    const [pdfPageRange, setPdfPageRange] = useState('all');
    const [pdfStartPage, setPdfStartPage] = useState(1);
    const [pdfEndPage, setPdfEndPage] = useState(1);

    // Shop selector state
    const [showShopSelector, setShowShopSelector] = useState(false);
    const [availableShops, setAvailableShops] = useState([]);
    const [loadingShops, setLoadingShops] = useState(false);

    // Modal drag and resize state
    const [modalPosition, setModalPosition] = useState({ x: 0, y: 0 });
    const [modalSize, setModalSize] = useState({ width: 700, height: 'auto' });
    const [isDragging, setIsDragging] = useState(false);
    const [isResizing, setIsResizing] = useState(false);
    const [currentWidthMode, setCurrentWidthMode] = useState('wide');
    const [isModalInitialized, setIsModalInitialized] = useState(false);

    const dragStart = useRef({ x: 0, y: 0 });
    const modalRef = useRef(null);
    const resizeType = useRef(null);
    const resizeStart = useRef({ x: 0, y: 0, width: 0, height: 0, left: 0, top: 0 });

    const widthModes = {
        narrow: { width: 400, label: 'Narrow', icon: faMinus },
        medium: { width: 650, label: 'Medium', icon: faArrowsAlt },
        wide: { width: 850, label: 'Wide', icon: faPlus },
        full: { width: '95vw', label: 'Full Width', icon: faExpand },
        fullscreen: { width: '98vw', label: 'Full Screen', icon: faCompress }
    };

    // Theme, Dark Mode, Language State
    const [currentLanguage, setCurrentLanguage] = useState(() => {
        return localStorage.getItem('ourzest_language') || 'en';
    });
    const [currentTheme, setCurrentTheme] = useState(() => {
        return localStorage.getItem('ourzest_theme') || '#6366f1';
    });
    const [darkMode, setDarkMode] = useState(() => {
        return localStorage.getItem('ourzest_darkMode') === 'true';
    });
    const [translations, setTranslations] = useState({});

    // Toast state
    const [toast, setToast] = useState({
        show: false,
        type: '',
        message: '',
        progress: 100,
        timeLeft: 3
    });

    const toastTimerRef = useRef(null);
    const isMountedRef = useRef(true);

    // Edit form state
    const [editFormData, setEditFormData] = useState({
        product_name: '',
        product_code: '',
        batch_no: '',
        barcode: '',
        category: '',
        company: '',
        packing_type: '',
        sub_packing_type: '',
        items_per_pack: '',
        unit_of_measure: '',
        sell_per_pack: '',
        total_quantity: '',
        quantity_alert: '',
        expiry_date: '',
        date: '',
        mrp: '',
        trade_percent: '',
        extra_percent: '',
        bonus_buy: '',
        bonus_free: '',
        effective_cost_price: '',
        effective_discount_percent: '',
        mrp_per_pack_unit: '',
        mrp_per_base_unit: '',
        cost_price_per_pack_unit: '',
        cost_price_per_base_unit: '',
        pack_content_quantity: '',
        pack_content_label: '',
        pack_base_unit_label: '',
        sell_price_per_item_unit: '',
        sell_price_per_base_unit: '',
        bonus_stock_quantity: '',
        actual_stock_quantity: '',
        purchased_quantity: ''
    });
    const [editFormErrors, setEditFormErrors] = useState({});

    // Business info state
    const [businessInfo, setBusinessInfo] = useState({
        businessname: '',
        bussinesslogo: '',
        businessaddress: '',
        fullname: '',
        email: ''
    });

    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(20);

    // Filter states
    const [filterBy, setFilterBy] = useState('');
    const [productNameFilter, setProductNameFilter] = useState('');
    const [productCodeFilter, setProductCodeFilter] = useState('');
    const [batchFilter, setBatchFilter] = useState('');
    const [categoryFilter, setCategoryFilter] = useState('');
    const [companyFilter, setCompanyFilter] = useState('');
    const [addedByFilter, setAddedByFilter] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [stockStatusFilter, setStockStatusFilter] = useState('');

    const escapeHtml = (text) => {
        if (!text) return '';
        return String(text).replace(/[&<>]/g, function (m) {
            if (m === '&') return '&amp;';
            if (m === '<') return '&lt;';
            if (m === '>') return '&gt;';
            return m;
        });
    };

    // ============================================
    // CHECK IF PRODUCT IS EXPIRED
    // ============================================
    const isExpired = useCallback((product) => {
        if (!product.expiry_date) return false;
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const expiry = new Date(product.expiry_date);
        expiry.setHours(0, 0, 0, 0);
        return expiry < today;
    }, []);

    // ============================================
    // GET DAYS OVERDUE
    // ============================================
    const getDaysOverdue = useCallback((expiryDate) => {
        if (!expiryDate) return 0;
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const expiry = new Date(expiryDate);
        expiry.setHours(0, 0, 0, 0);
        const diffTime = today - expiry;
        return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    }, []);

    // Options
    const categoryOptions = useMemo(() => [
        { value: '', label: 'All Categories' },
        { value: 'medicine', label: 'Medicine' },
        { value: 'surgical', label: 'Surgical' },
        { value: 'baby_care', label: 'Baby Care' },
        { value: 'personal_care', label: 'Personal Care' },
        { value: 'veterinary', label: 'Veterinary' },
        { value: 'herbal', label: 'Herbal' },
        { value: 'supplement', label: 'Supplement' },
        { value: 'medical_equipment', label: 'Medical Equipment' },
        { value: 'diagnostic', label: 'Diagnostic' },
        { value: 'first_aid', label: 'First Aid' },
        { value: 'others', label: 'Others' }
    ], []);

    const stockStatusOptions = useMemo(() => [
        { value: '', label: 'All Status' },
        { value: 'in_stock', label: 'In Stock' },
        { value: 'low_stock', label: 'Low Stock' },
        { value: 'out_of_stock', label: 'Out of Stock' }
    ], []);

    const pdfOrientationOptions = useMemo(() => [
        { value: 'portrait', label: translations.portrait || 'Portrait' },
        { value: 'landscape', label: translations.landscape || 'Landscape' }
    ], [translations]);

    const pdfPageSizeOptions = useMemo(() => [
        { value: 'a4', label: translations.a4 || 'A4' },
        { value: 'letter', label: translations.letter || 'Letter' },
        { value: 'legal', label: translations.legal || 'Legal' },
        { value: 'a5', label: translations.a5 || 'A5' }
    ], [translations]);

    const pdfPageRangeOptions = useMemo(() => [
        { value: 'all', label: 'All Pages' },
        { value: 'range', label: 'Page Range' }
    ], []);

    const filterByOptions = useMemo(() => [
        { value: 'all', label: translations.all || 'All' },
        { value: 'productName', label: translations.productNameFilter || 'Product Name' },
        { value: 'productCode', label: translations.productCodeFilter || 'Product Code' },
        { value: 'batch', label: translations.batchFilter || 'Batch Number' },
        { value: 'category', label: translations.categoryFilter || 'Category' },
        { value: 'company', label: translations.companyFilter || 'Company' },
        { value: 'date', label: translations.dateRange || 'Date Range' },
        { value: 'added_by', label: translations.addedByFilter || 'Added By' }
    ], [translations]);

    const showToast = useCallback((type, message) => {
        if (toastTimerRef.current) clearInterval(toastTimerRef.current);
        setToast({ show: true, type, message, progress: 100, timeLeft: 3 });
        let timeLeft = 3;
        const timer = setInterval(() => {
            timeLeft -= 0.1;
            const progress = (timeLeft / 3) * 100;
            setToast(prev => ({ ...prev, progress, timeLeft: parseFloat(timeLeft.toFixed(1)) }));
            if (timeLeft <= 0) {
                clearInterval(timer);
                setToast(prev => ({ ...prev, show: false }));
            }
        }, 100);
        toastTimerRef.current = timer;
    }, []);

    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
            if (toastTimerRef.current) clearInterval(toastTimerRef.current);
        };
    }, []);

    // ============================================
    // THEME SYNC
    // ============================================
    useEffect(() => {
        const applyTheme = (color) => {
            if (!color || !/^#[0-9A-F]{6}$/i.test(color)) color = '#6366f1';
            document.documentElement.style.setProperty('--ozlistui-primary', color);
            document.documentElement.style.setProperty('--ozlistui-primary-dark', color);
            document.documentElement.style.setProperty('--ozlistui-primary-light', color);
            setCurrentTheme(color);
        };

        applyTheme(currentTheme);

        const handleStorageChange = (e) => {
            if (e.key === 'ourzest_theme') {
                const newTheme = e.newValue || '#6366f1';
                applyTheme(newTheme);
            }
        };

        const handleThemeChanged = (event) => {
            if (event.detail && event.detail.theme) {
                applyTheme(event.detail.theme);
            }
        };

        window.addEventListener('storage', handleStorageChange);
        window.addEventListener('themeChanged', handleThemeChanged);

        const interval = setInterval(() => {
            const storedTheme = localStorage.getItem('ourzest_theme');
            if (storedTheme && storedTheme !== currentTheme) {
                applyTheme(storedTheme);
            }
        }, 1000);

        return () => {
            window.removeEventListener('storage', handleStorageChange);
            window.removeEventListener('themeChanged', handleThemeChanged);
            clearInterval(interval);
        };
    }, [currentTheme]);

    // ============================================
    // DARK MODE SYNC
    // ============================================
    useEffect(() => {
        const syncDarkMode = () => {
            const isDark = localStorage.getItem('ourzest_darkMode') === 'true';
            setDarkMode(isDark);

            const container = document.querySelector('.ozlistui-container');
            if (container) {
                if (isDark) {
                    container.classList.add('ozlistui-dark');
                } else {
                    container.classList.remove('ozlistui-dark');
                }
            }

            if (isDark) {
                document.body.classList.add('ozlistui-dark');
                document.documentElement.classList.add('ozlistui-dark');
            } else {
                document.body.classList.remove('ozlistui-dark');
                document.documentElement.classList.remove('ozlistui-dark');
            }
        };

        syncDarkMode();

        const handleStorageChange = (e) => {
            if (e.key === 'ourzest_darkMode') {
                syncDarkMode();
            }
        };

        const handleDarkModeToggle = (event) => {
            if (event.detail) {
                const isDark = event.detail.darkMode;
                setDarkMode(isDark);
                const container = document.querySelector('.ozlistui-container');
                if (container) {
                    if (isDark) {
                        container.classList.add('ozlistui-dark');
                    } else {
                        container.classList.remove('ozlistui-dark');
                    }
                }
                if (isDark) {
                    document.body.classList.add('ozlistui-dark');
                    document.documentElement.classList.add('ozlistui-dark');
                } else {
                    document.body.classList.remove('ozlistui-dark');
                    document.documentElement.classList.remove('ozlistui-dark');
                }
            } else {
                syncDarkMode();
            }
        };

        window.addEventListener('storage', handleStorageChange);
        window.addEventListener('darkModeToggled', handleDarkModeToggle);

        const interval = setInterval(() => {
            const storedDarkMode = localStorage.getItem('ourzest_darkMode') === 'true';
            if (storedDarkMode !== darkMode) {
                syncDarkMode();
            }
        }, 1000);

        return () => {
            window.removeEventListener('storage', handleStorageChange);
            window.removeEventListener('darkModeToggled', handleDarkModeToggle);
            clearInterval(interval);
        };
    }, [darkMode]);

    // ============================================
    // LANGUAGE SYNC
    // ============================================
    useEffect(() => {
        const loadTranslations = async (lang) => {
            const trans = await expiredProductTranslator.getTranslations(lang);
            setTranslations(trans);
        };

        const handleLanguageChange = () => {
            const lang = localStorage.getItem('ourzest_language') || 'en';
            if (lang !== currentLanguage) {
                setCurrentLanguage(lang);
                loadTranslations(lang);
            }
        };

        const handleCustomLanguageChange = (event) => {
            if (event.detail && event.detail.language) {
                const newLang = event.detail.language;
                if (newLang !== currentLanguage) {
                    setCurrentLanguage(newLang);
                    loadTranslations(newLang);
                }
            }
        };

        loadTranslations(currentLanguage);

        window.addEventListener('storage', handleLanguageChange);
        window.addEventListener('languageChanged', handleCustomLanguageChange);

        const interval = setInterval(() => {
            const storedLang = localStorage.getItem('ourzest_language') || 'en';
            if (storedLang !== currentLanguage) {
                setCurrentLanguage(storedLang);
                loadTranslations(storedLang);
            }
        }, 1000);

        return () => {
            window.removeEventListener('storage', handleLanguageChange);
            window.removeEventListener('languageChanged', handleCustomLanguageChange);
            clearInterval(interval);
        };
    }, [currentLanguage]);

    // ============================================
    // RTL DIRECTION SYNC
    // ============================================
    useEffect(() => {
        const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';
        const container = document.querySelector('.ozlistui-container');

        if (container) {
            if (isRTL) {
                container.classList.add('ozlistui-rtl');
                container.classList.remove('ozlistui-ltr');
            } else {
                container.classList.add('ozlistui-ltr');
                container.classList.remove('ozlistui-rtl');
            }
        }

        if (isRTL) {
            document.documentElement.dir = 'rtl';
            document.body.dir = 'rtl';
        } else {
            document.documentElement.dir = 'ltr';
            document.body.dir = 'ltr';
        }
    }, [currentLanguage]);

    // Modal functions
    const centerModal = useCallback(() => {
        if (!modalRef.current) return;
        const rect = modalRef.current.getBoundingClientRect();
        const left = (window.innerWidth - rect.width) / 2;
        const top = (window.innerHeight - rect.height) / 2;
        modalRef.current.style.left = `${Math.max(0, left)}px`;
        modalRef.current.style.top = `${Math.max(0, top)}px`;
        modalRef.current.style.transform = 'none';
        setModalPosition({ x: Math.max(0, left), y: Math.max(0, top) });
    }, []);

    const setWidthMode = useCallback((mode) => {
        if (!modalRef.current) return;
        setCurrentWidthMode(mode);
        const widthValue = widthModes[mode].width;
        if (mode === 'fullscreen') {
            modalRef.current.style.width = '98vw';
            modalRef.current.style.height = '90vh';
        } else {
            modalRef.current.style.width = typeof widthValue === 'number' ? `${widthValue}px` : widthValue;
            modalRef.current.style.height = 'auto';
        }
        setModalSize({ width: modalRef.current.offsetWidth, height: modalRef.current.offsetHeight });
        setTimeout(() => centerModal(), 10);
    }, [widthModes, centerModal]);

    const startDrag = useCallback((e) => {
        if (!modalRef.current) return;
        if (e.target.closest('button, input, select, textarea, a, .ozlistui-width-btn, .ozlistui-close-btn')) return;
        e.preventDefault();
        setIsDragging(true);
        const rect = modalRef.current.getBoundingClientRect();
        dragStart.current = { x: e.clientX - rect.left, y: e.clientY - rect.top };
        modalRef.current.style.transition = 'none';
    }, []);

    const startResize = useCallback((e, type) => {
        e.preventDefault();
        e.stopPropagation();
        if (!modalRef.current) return;
        setIsResizing(true);
        resizeType.current = type;
        const rect = modalRef.current.getBoundingClientRect();
        resizeStart.current = {
            x: e.clientX, y: e.clientY,
            width: rect.width, height: rect.height,
            left: rect.left, top: rect.top,
            minWidth: 350, minHeight: 400,
            maxWidth: window.innerWidth - 40, maxHeight: window.innerHeight - 40
        };
        modalRef.current.style.transition = 'none';
    }, []);

    useEffect(() => {
        const handleMouseMove = (e) => {
            if (isDragging && modalRef.current) {
                let newLeft = e.clientX - dragStart.current.x;
                let newTop = e.clientY - dragStart.current.y;
                const rect = modalRef.current.getBoundingClientRect();
                newLeft = Math.max(0, Math.min(newLeft, window.innerWidth - rect.width));
                newTop = Math.max(0, Math.min(newTop, window.innerHeight - rect.height));
                modalRef.current.style.left = `${newLeft}px`;
                modalRef.current.style.top = `${newTop}px`;
                modalRef.current.style.transform = 'none';
                setModalPosition({ x: newLeft, y: newTop });
            }

            if (isResizing && modalRef.current && resizeType.current) {
                let newWidth = resizeStart.current.width;
                let newHeight = resizeStart.current.height;
                let newLeft = resizeStart.current.left;
                let newTop = resizeStart.current.top;
                const deltaX = e.clientX - resizeStart.current.x;
                const deltaY = e.clientY - resizeStart.current.y;

                switch (resizeType.current) {
                    case 'e': newWidth = Math.min(resizeStart.current.maxWidth, Math.max(resizeStart.current.minWidth, resizeStart.current.width + deltaX)); break;
                    case 'w': newWidth = Math.min(resizeStart.current.maxWidth, Math.max(resizeStart.current.minWidth, resizeStart.current.width - deltaX)); newLeft = resizeStart.current.left + (resizeStart.current.width - newWidth); break;
                    case 's': newHeight = Math.min(resizeStart.current.maxHeight, Math.max(resizeStart.current.minHeight, resizeStart.current.height + deltaY)); break;
                    case 'n': newHeight = Math.min(resizeStart.current.maxHeight, Math.max(resizeStart.current.minHeight, resizeStart.current.height - deltaY)); newTop = resizeStart.current.top + (resizeStart.current.height - newHeight); break;
                    case 'ne': newWidth = Math.min(resizeStart.current.maxWidth, Math.max(resizeStart.current.minWidth, resizeStart.current.width + deltaX)); newHeight = Math.min(resizeStart.current.maxHeight, Math.max(resizeStart.current.minHeight, resizeStart.current.height - deltaY)); newTop = resizeStart.current.top + (resizeStart.current.height - newHeight); break;
                    case 'nw': newWidth = Math.min(resizeStart.current.maxWidth, Math.max(resizeStart.current.minWidth, resizeStart.current.width - deltaX)); newHeight = Math.min(resizeStart.current.maxHeight, Math.max(resizeStart.current.minHeight, resizeStart.current.height - deltaY)); newLeft = resizeStart.current.left + (resizeStart.current.width - newWidth); newTop = resizeStart.current.top + (resizeStart.current.height - newHeight); break;
                    case 'se': newWidth = Math.min(resizeStart.current.maxWidth, Math.max(resizeStart.current.minWidth, resizeStart.current.width + deltaX)); newHeight = Math.min(resizeStart.current.maxHeight, Math.max(resizeStart.current.minHeight, resizeStart.current.height + deltaY)); break;
                    case 'sw': newWidth = Math.min(resizeStart.current.maxWidth, Math.max(resizeStart.current.minWidth, resizeStart.current.width - deltaX)); newHeight = Math.min(resizeStart.current.maxHeight, Math.max(resizeStart.current.minHeight, resizeStart.current.height + deltaY)); newLeft = resizeStart.current.left + (resizeStart.current.width - newWidth); break;
                }
                modalRef.current.style.width = `${newWidth}px`;
                modalRef.current.style.height = `${newHeight}px`;
                modalRef.current.style.left = `${newLeft}px`;
                modalRef.current.style.top = `${newTop}px`;
                setModalSize({ width: newWidth, height: newHeight });
                setModalPosition({ x: newLeft, y: newTop });
            }
        };

        const handleMouseUp = () => {
            if (isDragging || isResizing) {
                setIsDragging(false);
                setIsResizing(false);
                resizeType.current = null;
                if (modalRef.current) modalRef.current.style.transition = '';
            }
        };

        if (isDragging || isResizing) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
        }

        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isDragging, isResizing]);

    useEffect(() => {
        if (showEditModal && modalRef.current && !isModalInitialized) {
            setTimeout(() => { centerModal(); setWidthMode('wide'); setIsModalInitialized(true); }, 50);
        }
        if (!showEditModal) setIsModalInitialized(false);
    }, [showEditModal, centerModal, setWidthMode, isModalInitialized]);

    useEffect(() => {
        const handleWindowResize = () => {
            if (!modalRef.current || !showEditModal) return;
            const rect = modalRef.current.getBoundingClientRect();
            let newLeft = Math.min(rect.left, window.innerWidth - rect.width);
            let newTop = Math.min(rect.top, window.innerHeight - rect.height);
            newLeft = Math.max(0, newLeft);
            newTop = Math.max(0, newTop);
            if (newLeft !== rect.left || newTop !== rect.top) {
                modalRef.current.style.left = `${newLeft}px`;
                modalRef.current.style.top = `${newTop}px`;
                setModalPosition({ x: newLeft, y: newTop });
            }
        };
        window.addEventListener('resize', handleWindowResize);
        return () => window.removeEventListener('resize', handleWindowResize);
    }, [showEditModal]);

    // ============================================
    // RESET FILTERS
    // ============================================
    const resetFilters = useCallback(() => {
        setFilterBy('');
        setProductNameFilter('');
        setProductCodeFilter('');
        setBatchFilter('');
        setCategoryFilter('');
        setCompanyFilter('');
        setAddedByFilter('');
        setStartDate('');
        setEndDate('');
        setSearchTerm('');
        setStockStatusFilter('');
    }, []);

    // ============================================
    // FETCH PRODUCTS
    // ============================================
    const fetchProducts = useCallback(async () => {
        if (!user || !user.username) {
            if (isMountedRef.current) {
                showToast('error', 'User information not available');
                setLoading(false);
            }
            return;
        }
        if (!currentShop || !currentShop.shop_id) {
            if (isMountedRef.current) {
                showToast('error', translations.noShopSelected || 'Please select a shop first');
                setLoading(false);
            }
            return;
        }

        if (isMountedRef.current) {
            setLoading(true);
            setFetchError('');
        }

        const payload = {
            action: 'get_records',
            username: user.username,
            shop_id: currentShop.shop_id,
            limit: 10000,
            offset: 0
        };

        try {
            const response = await fetch(PRODUCTS_ENDPOINT, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            const rawText = await response.text();
            let data;
            try {
                data = JSON.parse(rawText);
            } catch (parseErr) {
                console.error('[PharmacyExpiredProducts] Server did not return valid JSON. Raw response:', rawText.substring(0, 1000));
                throw new Error('Server returned an invalid response');
            }

            if (!response.ok) {
                console.warn('[PharmacyExpiredProducts] HTTP status', response.status, data);
            }

            if (!isMountedRef.current) return;

            if (data.status === 'success') {
                const rows = Array.isArray(data.data) ? data.data : [];
                setAllProducts(rows);
                
                // Filter products that are expired
                const expiredRows = rows.filter(product => isExpired(product));
                setExpiredProducts(expiredRows);
                
                if (expiredRows.length === 0) {
                    console.info('[PharmacyExpiredProducts] No expired products found for shop_id =', currentShop.shop_id);
                }
            } else {
                console.error('[PharmacyExpiredProducts] API returned error:', data.message, data);
                setFetchError(data.message || 'Failed to load products');
                showToast('error', data.message || translations.networkError || 'Failed to load products');
                setAllProducts([]);
                setExpiredProducts([]);
            }
        } catch (error) {
            console.error('[PharmacyExpiredProducts] fetchProducts failed:', error);
            if (isMountedRef.current) {
                setFetchError(error.message || 'Network error occurred');
                showToast('error', error.message || translations.networkError || 'Network error occurred');
            }
        } finally {
            if (isMountedRef.current) setLoading(false);
        }
    }, [user, currentShop, translations, showToast, isExpired]);

    const fetchAvailableShops = useCallback(async () => {
        if (!user) return;
        setLoadingShops(true);
        try {
            const shopsData = await getShopsForSwitch();
            if (isMountedRef.current) setAvailableShops(shopsData.shops || []);
        } catch (error) {
            console.error('Error loading shops:', error);
            if (isMountedRef.current) setAvailableShops([]);
        } finally {
            if (isMountedRef.current) setLoadingShops(false);
        }
    }, [user, getShopsForSwitch]);

    const fetchBusinessInfo = useCallback(async () => {
        if (!user || !user.username) return;
        try {
            const response = await fetch(BUSINESS_INFO_ENDPOINT, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ action: 'get_business_info', username: user.username })
            });
            const data = await response.json();
            if (isMountedRef.current) {
                if (data.status === 'success') {
                    setBusinessInfo({
                        businessname: data.businessname || '',
                        bussinesslogo: data.bussinesslogo || '',
                        businessaddress: data.businessaddress || '',
                        fullname: data.fullname || '',
                        email: data.email || ''
                    });
                } else {
                    setBusinessInfo({
                        businessname: user.business_name || '',
                        bussinesslogo: '',
                        businessaddress: '',
                        fullname: user.fullname || '',
                        email: user.email || ''
                    });
                }
            }
        } catch (error) {
            console.error('Error fetching business info:', error);
            if (isMountedRef.current) {
                setBusinessInfo({
                    businessname: user.business_name || '',
                    bussinesslogo: '',
                    businessaddress: '',
                    fullname: user.fullname || '',
                    email: user.email || ''
                });
            }
        }
    }, [user]);

    useEffect(() => {
        if (user && user.username && currentShop) {
            fetchProducts();
            fetchBusinessInfo();
            fetchAvailableShops();
        } else if (user && user.username && !currentShop) {
            setLoading(false);
        } else if (!user) {
            setLoading(false);
        }
    }, [user, currentShop?.shop_id]);

    const handleSwitchShop = useCallback(async (shop) => {
        try {
            const success = await switchShop(shop.shop_id, shop.shop_name, shop.shop_code);
            if (success) {
                showToast('success', 'Shop switched successfully');
                setCurrentPage(1);
                resetFilters();
                setShowShopSelector(false);
            } else {
                showToast('error', 'Failed to switch shop');
            }
        } catch (error) {
            console.error('Error switching shop:', error);
            showToast('error', 'Failed to switch shop');
        }
    }, [switchShop, showToast, resetFilters]);

    // ============================================
    // FILTERING
    // ============================================
    const applyFilters = useCallback(() => {
        let filtered = [...expiredProducts];

        if (searchTerm.trim()) {
            const term = searchTerm.toLowerCase();
            filtered = filtered.filter(product =>
                product.product_name?.toLowerCase().includes(term) ||
                product.product_code?.toLowerCase().includes(term) ||
                product.batch_no?.toLowerCase().includes(term) ||
                product.barcode?.toLowerCase().includes(term) ||
                product.company?.toLowerCase().includes(term) ||
                product.category?.toLowerCase().includes(term) ||
                product.username?.toLowerCase().includes(term)
            );
        }

        if (filterBy === 'productName' && productNameFilter.trim()) {
            const term = productNameFilter.toLowerCase();
            filtered = filtered.filter(product => product.product_name?.toLowerCase().includes(term));
        }
        if (filterBy === 'productCode' && productCodeFilter.trim()) {
            const term = productCodeFilter.toLowerCase();
            filtered = filtered.filter(product => product.product_code?.toLowerCase().includes(term));
        }
        if (filterBy === 'batch' && batchFilter.trim()) {
            const term = batchFilter.toLowerCase();
            filtered = filtered.filter(product => product.batch_no?.toLowerCase().includes(term));
        }
        if (filterBy === 'category' && categoryFilter) {
            filtered = filtered.filter(product => product.category === categoryFilter);
        }
        if (filterBy === 'company' && companyFilter.trim()) {
            const term = companyFilter.toLowerCase();
            filtered = filtered.filter(product => product.company?.toLowerCase().includes(term));
        }
        if (filterBy === 'added_by' && addedByFilter.trim()) {
            const term = addedByFilter.toLowerCase();
            filtered = filtered.filter(product => product.username?.toLowerCase().includes(term));
        }

        if (startDate) {
            const start = new Date(startDate);
            start.setHours(0, 0, 0, 0);
            filtered = filtered.filter(product => {
                if (!product.date) return false;
                const productDate = new Date(product.date);
                productDate.setHours(0, 0, 0, 0);
                return productDate >= start;
            });
        }
        if (endDate) {
            const end = new Date(endDate);
            end.setHours(23, 59, 59, 999);
            filtered = filtered.filter(product => {
                if (!product.date) return false;
                const productDate = new Date(product.date);
                return productDate <= end;
            });
        }

        if (stockStatusFilter) {
            filtered = filtered.filter(product => {
                const qty = parseFloat(product.total_quantity) || 0;
                const alert = parseFloat(product.quantity_alert) || 0;
                if (stockStatusFilter === 'in_stock') return qty > alert && qty > 0;
                if (stockStatusFilter === 'low_stock') return qty <= alert && qty > 0;
                if (stockStatusFilter === 'out_of_stock') return qty <= 0;
                return true;
            });
        }

        setFilteredProducts(filtered);
        setCurrentPage(1);
    }, [expiredProducts, searchTerm, filterBy, productNameFilter, productCodeFilter, batchFilter, categoryFilter, companyFilter, addedByFilter, startDate, endDate, stockStatusFilter]);

    useEffect(() => { applyFilters(); }, [applyFilters]);

    // ============================================
    // PAGINATION
    // ============================================
    const getRowsPerPage = useCallback(() => {
        if (pdfOrientation === 'landscape') return 15;
        return 12;
    }, [pdfOrientation]);

    const pagination = useMemo(() => {
        const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);
        const startIndex = (currentPage - 1) * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;
        const currentProducts = filteredProducts.slice(startIndex, endIndex);
        return { totalPages, startIndex, endIndex, currentProducts };
    }, [filteredProducts, currentPage, itemsPerPage]);

    // ============================================
    // PDF EXPORT
    // ============================================
    const exportToPDF = useCallback(async () => {
        if (!filteredProducts || filteredProducts.length === 0) {
            showToast('warning', translations.noDataToExport || 'No data to export');
            return;
        }
        setPdfLoading(true);
        try {
            const currentLang = currentLanguage;
            const isRTL = currentLang === 'ur' || currentLang === 'ar';

            const pdfTextColor = '#1f2937';
            const pdfSubTextColor = '#6b7280';
            const pdfBorderColor = '#e5e7eb';
            const pdfHeaderBgColor = '#f9fafb';
            const pdfRowEvenBg = '#ffffff';
            const pdfRowOddBg = '#f9fafb';
            const pdfThemeColor = currentTheme;

            const pageSizeMap = { 'a4': 'a4', 'letter': 'letter', 'legal': 'legal', 'a5': 'a5' };
            const pageFormat = pageSizeMap[pdfPageSize] || 'a4';
            const pdf = new jsPDF({ orientation: pdfOrientation, unit: 'mm', format: pageFormat, compress: true });
            const pageWidth = pdf.internal.pageSize.getWidth();
            const rowsPerPage = getRowsPerPage();

            let productsToExport = [...filteredProducts];

            if (pdfPageRange === 'range') {
                const totalPagesForRange = Math.ceil(productsToExport.length / rowsPerPage);
                const start = Math.max(1, pdfStartPage);
                const end = Math.min(totalPagesForRange, pdfEndPage);
                const startIdx = (start - 1) * rowsPerPage;
                const endIdx = Math.min(end * rowsPerPage, productsToExport.length);
                productsToExport = productsToExport.slice(startIdx, endIdx);
            }

            const totalPages = Math.ceil(productsToExport.length / rowsPerPage);

            const getPDFTranslation = (key, fallback = '') => {
                return translations[key] || fallback;
            };

            const renderPage = async (pageProducts, pageNum, startRowIndex) => {
                return new Promise((resolve, reject) => {
                    try {
                        const container = document.createElement('div');
                        container.style.width = '1100px';
                        container.style.padding = '12px 15px';
                        container.style.backgroundColor = '#ffffff';
                        container.style.color = pdfTextColor;
                        container.style.position = 'absolute';
                        container.style.left = '-9999px';
                        container.style.top = '0';
                        container.style.fontFamily = "'Segoe UI', 'Helvetica Neue', Arial, sans-serif";
                        container.style.direction = isRTL ? 'rtl' : 'ltr';
                        container.style.fontSize = '8px';

                        const productReportLabel = getPDFTranslation('productReport', 'Expired Product Report');
                        const productDirectoryLabel = getPDFTranslation('productDirectory', 'Expired Products Directory');
                        const totalProductsLabel = getPDFTranslation('totalProducts', 'Total Expired');
                        const generatedLabel = getPDFTranslation('generated', 'Generated');
                        const reportIdLabel = getPDFTranslation('reportId', 'Report ID');
                        const generatedByLabel = getPDFTranslation('generatedBy', 'Generated by');
                        const shopCodeLabel = getPDFTranslation('shopCode', 'Shop Code');
                        const filtersAppliedLabel = getPDFTranslation('filtersApplied', 'FILTERS APPLIED');
                        const searchLabel = getPDFTranslation('search', 'Search');
                        const categoryLabel = getPDFTranslation('category', 'Category');
                        const fromLabel = getPDFTranslation('from', 'From');
                        const toLabel = getPDFTranslation('to', 'To');
                        const pageLabel = getPDFTranslation('page', 'Page');
                        const ofLabel = getPDFTranslation('of', 'of');

                        const headerHTML = `
                            <div style="margin-bottom: 8px; text-align: center;">
                                <div style="font-size: 7px; letter-spacing: 2px; color: ${pdfSubTextColor}; margin-bottom: 3px; text-transform: uppercase; font-weight: 500;">${productReportLabel}</div>
                                <h1 style="color: ${pdfTextColor}; font-size: 16px; margin: 0 0 3px 0; font-weight: 600;">
                                    ${escapeHtml(currentShop?.shop_name) || escapeHtml(productDirectoryLabel)}
                                    <span style="font-size: 12px; color: #dc2626; margin-left: 10px;">⚠️ Expired</span>
                                </h1>
                                <div style="width: 30px; height: 2px; background: #dc2626; margin: 4px auto;"></div>
                                ${businessInfo.businessname ? `<div style="color: ${pdfSubTextColor}; font-size: 8px; margin-top: 3px;">${escapeHtml(businessInfo.businessname)}</div>` : ''}
                                <div style="color: ${pdfSubTextColor}; font-size: 6px; margin-top: 3px;">
                                    ${totalProductsLabel}: ${productsToExport.length} | ${generatedLabel}: ${new Date().toLocaleString(currentLang)}
                                </div>
                            </div>
                            <div style="display: flex; justify-content: space-between; margin: 5px 0 6px 0; padding: 4px 0; border-top: 1px solid ${pdfBorderColor}; border-bottom: 1px solid ${pdfBorderColor}; ${isRTL ? 'flex-direction: row-reverse;' : ''}">
                                <div style="font-size: 5.5px; color: ${pdfSubTextColor};"><strong>${reportIdLabel}:</strong> #${new Date().toISOString().split('T')[0].replace(/-/g, '')}-${String(pageNum).padStart(3, '0')}</div>
                                <div style="font-size: 5.5px; color: ${pdfSubTextColor};"><strong>${generatedByLabel}:</strong> ${escapeHtml(user?.fullname || user?.username || 'System')}</div>
                                <div style="font-size: 5.5px; color: ${pdfSubTextColor};"><strong>${shopCodeLabel}:</strong> ${escapeHtml(currentShop?.shop_code || 'N/A')}</div>
                            </div>
                        `;

                        const hasFilters = searchTerm || categoryFilter || startDate || endDate;
                        const filterHTML = hasFilters ? `
                            <div style="margin: 4px 0; padding: 3px 8px; background: #f7f7f7; border-${isRTL ? 'right' : 'left'}: 3px solid #dc2626; font-size: 5.5px; color: ${pdfSubTextColor};">
                                <span style="font-weight: 600;">${filtersAppliedLabel}:</span> 
                                ${searchTerm ? `${searchLabel}: "${escapeHtml(searchTerm)}"` : ''}
                                ${categoryFilter ? ` | ${categoryLabel}: ${escapeHtml(categoryFilter)}` : ''}
                                ${startDate ? ` | ${fromLabel}: ${startDate}` : ''}
                                ${endDate ? ` | ${toLabel}: ${endDate}` : ''}
                            </div>
                        ` : '';

                        let tableHTML = `
                            <table style="width: 100%; border-collapse: collapse; font-size: 5.5px; margin-top: 3px; table-layout: fixed;">
                                <thead>
                                    <tr style="background-color: ${pdfHeaderBgColor}; border-bottom: 2px solid #dc2626; height: 22px;">
                                        <th style="padding: 4px 2px; text-align: center; font-weight: 700; color: ${pdfTextColor}; border-right: 1px solid ${pdfBorderColor}; width: 2.5%; vertical-align: middle; height: 22px;">#</th>
                                        <th style="padding: 4px 2px; text-align: ${isRTL ? 'right' : 'left'}; font-weight: 700; color: ${pdfTextColor}; border-right: 1px solid ${pdfBorderColor}; width: 10%; vertical-align: middle; height: 22px;">Name</th>
                                        <th style="padding: 4px 2px; text-align: center; font-weight: 700; color: ${pdfTextColor}; border-right: 1px solid ${pdfBorderColor}; width: 5%; vertical-align: middle; height: 22px;">Code</th>
                                        <th style="padding: 4px 2px; text-align: center; font-weight: 700; color: ${pdfTextColor}; border-right: 1px solid ${pdfBorderColor}; width: 5%; vertical-align: middle; height: 22px;">Batch</th>
                                        <th style="padding: 4px 2px; text-align: center; font-weight: 700; color: ${pdfTextColor}; border-right: 1px solid ${pdfBorderColor}; width: 5%; vertical-align: middle; height: 22px;">Unit</th>
                                        <th style="padding: 4px 2px; text-align: right; font-weight: 700; color: ${pdfTextColor}; border-right: 1px solid ${pdfBorderColor}; width: 5%; vertical-align: middle; height: 22px;">MRP</th>
                                        <th style="padding: 4px 2px; text-align: right; font-weight: 700; color: ${pdfTextColor}; border-right: 1px solid ${pdfBorderColor}; width: 5%; vertical-align: middle; height: 22px;">Retail</th>
                                        <th style="padding: 4px 2px; text-align: center; font-weight: 700; color: ${pdfTextColor}; border-right: 1px solid ${pdfBorderColor}; width: 6%; vertical-align: middle; height: 22px;">Expiry Date</th>
                                        <th style="padding: 4px 2px; text-align: center; font-weight: 700; color: ${pdfTextColor}; border-right: 1px solid ${pdfBorderColor}; width: 5%; vertical-align: middle; height: 22px;">Overdue</th>
                                        <th style="padding: 4px 2px; text-align: right; font-weight: 700; color: ${pdfTextColor}; border-right: 1px solid ${pdfBorderColor}; width: 4%; vertical-align: middle; height: 22px;">Qty</th>
                                        <th style="padding: 4px 2px; text-align: center; font-weight: 700; color: ${pdfTextColor}; width: 5%; vertical-align: middle; height: 22px;">Added By</th>
                                    </tr>
                                </thead>
                                <tbody>
                        `;

                        pageProducts.forEach((product, idx) => {
                            const rowNum = startRowIndex + idx + 1;
                            const isEven = idx % 2 === 0;
                            const daysOverdue = getDaysOverdue(product.expiry_date);
                            
                            tableHTML += `
                                <tr style="border-bottom: 0.5px solid ${pdfBorderColor}; background: ${isEven ? pdfRowEvenBg : pdfRowOddBg}; height: 20px;">
                                    <td style="padding: 3px 2px; text-align: center; color: ${pdfSubTextColor}; border-right: 0.5px solid ${pdfBorderColor}; vertical-align: middle; height: 20px;">${rowNum}</td>
                                    <td style="padding: 3px 2px; text-align: ${isRTL ? 'right' : 'left'}; color: ${pdfTextColor}; font-weight: 500; border-right: 0.5px solid ${pdfBorderColor}; vertical-align: middle; height: 20px; word-wrap: break-word;">${escapeHtml(product.product_name) || '-'}</td>
                                    <td style="padding: 3px 2px; text-align: center; color: ${pdfTextColor}; border-right: 0.5px solid ${pdfBorderColor}; vertical-align: middle; height: 20px;">${escapeHtml(product.product_code) || '—'}</td>
                                    <td style="padding: 3px 2px; text-align: center; color: ${pdfTextColor}; border-right: 0.5px solid ${pdfBorderColor}; vertical-align: middle; height: 20px;">${escapeHtml(product.batch_no) || '—'}</td>
                                    <td style="padding: 3px 2px; text-align: center; color: ${pdfTextColor}; border-right: 0.5px solid ${pdfBorderColor}; vertical-align: middle; height: 20px;">${getUnitDisplayLabel(product.unit_of_measure) || '—'}</td>
                                    <td style="padding: 3px 2px; text-align: right; color: ${pdfTextColor}; border-right: 0.5px solid ${pdfBorderColor}; font-family: monospace; vertical-align: middle; height: 20px;">${product.mrp ? `Rs.${Number(product.mrp).toFixed(2)}` : '—'}</td>
                                    <td style="padding: 3px 2px; text-align: right; color: ${pdfTextColor}; border-right: 0.5px solid ${pdfBorderColor}; font-family: monospace; vertical-align: middle; height: 20px;">${product.sell_per_pack ? `Rs.${Number(product.sell_per_pack).toFixed(2)}` : '—'}</td>
                                    <td style="padding: 3px 2px; text-align: center; color: #dc2626; font-weight: bold; border-right: 0.5px solid ${pdfBorderColor}; vertical-align: middle; height: 20px;">${formatDate(product.expiry_date)}</td>
                                    <td style="padding: 3px 2px; text-align: center; color: #dc2626; font-weight: bold; border-right: 0.5px solid ${pdfBorderColor}; vertical-align: middle; height: 20px;">${daysOverdue} days</td>
                                    <td style="padding: 3px 2px; text-align: right; color: ${pdfTextColor}; border-right: 0.5px solid ${pdfBorderColor}; font-family: monospace; vertical-align: middle; height: 20px;">${product.total_quantity ? Number(product.total_quantity).toFixed(2) : '—'}</td>
                                    <td style="padding: 3px 2px; text-align: center; color: ${pdfTextColor}; vertical-align: middle; height: 20px;">${escapeHtml(product.username) || '—'}</td>
                                </tr>
                            `;
                        });

                        const emptyRowsNeeded = rowsPerPage - pageProducts.length;
                        for (let i = 0; i < emptyRowsNeeded; i++) {
                            tableHTML += `
                                <tr style="border-bottom: 0.5px solid ${pdfBorderColor}; background: ${(pageProducts.length + i) % 2 === 0 ? pdfRowEvenBg : pdfRowOddBg}; height: 20px;">
                                    <td style="padding: 3px 2px; text-align: center; color: ${pdfSubTextColor}; border-right: 0.5px solid ${pdfBorderColor}; vertical-align: middle; height: 20px;">&nbsp;</td>
                                    <td style="padding: 3px 2px; border-right: 0.5px solid ${pdfBorderColor}; vertical-align: middle; height: 20px;">&nbsp;</td>
                                    <td style="padding: 3px 2px; border-right: 0.5px solid ${pdfBorderColor}; vertical-align: middle; height: 20px;">&nbsp;</td>
                                    <td style="padding: 3px 2px; border-right: 0.5px solid ${pdfBorderColor}; vertical-align: middle; height: 20px;">&nbsp;</td>
                                    <td style="padding: 3px 2px; border-right: 0.5px solid ${pdfBorderColor}; vertical-align: middle; height: 20px;">&nbsp;</td>
                                    <td style="padding: 3px 2px; border-right: 0.5px solid ${pdfBorderColor}; vertical-align: middle; height: 20px;">&nbsp;</td>
                                    <td style="padding: 3px 2px; border-right: 0.5px solid ${pdfBorderColor}; vertical-align: middle; height: 20px;">&nbsp;</td>
                                    <td style="padding: 3px 2px; border-right: 0.5px solid ${pdfBorderColor}; vertical-align: middle; height: 20px;">&nbsp;</td>
                                    <td style="padding: 3px 2px; border-right: 0.5px solid ${pdfBorderColor}; vertical-align: middle; height: 20px;">&nbsp;</td>
                                    <td style="padding: 3px 2px; border-right: 0.5px solid ${pdfBorderColor}; vertical-align: middle; height: 20px;">&nbsp;</td>
                                    <td style="padding: 3px 2px; vertical-align: middle; height: 20px;">&nbsp;</td>
                                </tr>
                            `;
                        }

                        tableHTML += `
                                </tbody>
                            </table>
                        `;

                        const footerHTML = `
                            <div style="margin-top: 6px; padding-top: 4px; text-align: center;">
                                <div style="width: 30px; height: 1px; background: ${pdfBorderColor}; margin: 0 auto 4px auto;"></div>
                                <div style="font-size: 5.5px; color: ${pdfSubTextColor};">${pageLabel} ${pageNum} ${ofLabel} ${totalPages}</div>
                            </div>
                        `;

                        container.innerHTML = headerHTML + filterHTML + tableHTML + footerHTML;
                        document.body.appendChild(container);
                        html2canvas(container, { scale: 2, backgroundColor: '#ffffff', logging: false }).then(canvas => {
                            document.body.removeChild(container);
                            resolve(canvas);
                        }).catch(err => { document.body.removeChild(container); reject(err); });
                    } catch (err) { reject(err); }
                });
            };

            for (let pageNum = 1; pageNum <= totalPages; pageNum++) {
                const startRow = (pageNum - 1) * rowsPerPage;
                const endRow = Math.min(pageNum * rowsPerPage, productsToExport.length);
                const pageProducts = productsToExport.slice(startRow, endRow);
                const canvas = await renderPage(pageProducts, pageNum, startRow);
                if (pageNum > 1) {
                    pdf.addPage();
                }
                const imgWidth = pageWidth - 20;
                const imgHeight = (canvas.height * imgWidth) / canvas.width;
                pdf.addImage(canvas.toDataURL('image/png'), 'PNG', 10, 5, imgWidth, imgHeight);
            }

            const fileName = `Expired_Product_Report_${currentShop?.shop_name?.replace(/[^a-z0-9]/gi, '_') || 'Export'}_${new Date().toISOString().split('T')[0]}.pdf`;
            pdf.save(fileName);
            showToast('success', `${translations.reportGenerated || 'Report generated!'} ${productsToExport.length} ${translations.products || 'products'} ${translations.on || 'on'} ${totalPages} ${translations.pages || 'page(s)'}`);
        } catch (error) {
            console.error('PDF Error:', error);
            showToast('error', translations.pdfGenerationError || 'Error generating PDF');
        } finally {
            setPdfLoading(false);
        }
    }, [filteredProducts, currentLanguage, pdfOrientation, pdfPageSize, pdfPageRange, pdfStartPage, pdfEndPage, translations, businessInfo, currentShop, user, showToast, getRowsPerPage, searchTerm, categoryFilter, startDate, endDate, currentTheme, getDaysOverdue]);

    // ============================================
    // HANDLE ACTIONS
    // ============================================
    const handleView = useCallback((product) => { setSelectedProduct(product); setShowViewModal(true); }, []);
    const handleEdit = useCallback((product) => {
        setSelectedProduct(product);
        setEditFormData({
            product_name: product.product_name || '',
            product_code: product.product_code || '',
            batch_no: product.batch_no || '',
            barcode: product.barcode || '',
            category: product.category || '',
            company: product.company || '',
            packing_type: product.packing_type || '',
            sub_packing_type: product.sub_packing_type || '',
            items_per_pack: product.items_per_pack || '',
            unit_of_measure: product.unit_of_measure || '',
            sell_per_pack: product.sell_per_pack || '',
            total_quantity: product.total_quantity || '',
            quantity_alert: product.quantity_alert || '',
            expiry_date: product.expiry_date || '',
            date: product.date || '',
            mrp: product.mrp || '',
            trade_percent: product.trade_percent || '',
            extra_percent: product.extra_percent || '',
            bonus_buy: product.bonus_buy || '',
            bonus_free: product.bonus_free || '',
            effective_cost_price: product.effective_cost_price || '',
            effective_discount_percent: product.effective_discount_percent || '',
            mrp_per_pack_unit: product.mrp_per_pack_unit || '',
            mrp_per_base_unit: product.mrp_per_base_unit || '',
            cost_price_per_pack_unit: product.cost_price_per_pack_unit || '',
            cost_price_per_base_unit: product.cost_price_per_base_unit || '',
            pack_content_quantity: product.pack_content_quantity || '',
            pack_content_label: product.pack_content_label || '',
            pack_base_unit_label: product.pack_base_unit_label || '',
            sell_price_per_item_unit: product.sell_price_per_item_unit || '',
            sell_price_per_base_unit: product.sell_price_per_base_unit || '',
            bonus_stock_quantity: product.bonus_stock_quantity || '',
            actual_stock_quantity: product.actual_stock_quantity || '',
            purchased_quantity: product.purchased_quantity || ''
        });
        setEditFormErrors({});
        setShowEditModal(true);
    }, []);

    const handleDeleteClick = useCallback((productId) => {
        const product = allProducts.find(p => p.id === productId);
        if (!product) return;
        if (product.username !== user?.username) {
            showToast('error', 'You can only delete products you added');
            return;
        }
        setProductToDelete(productId);
        setShowDeleteModal(true);
    }, [allProducts, user, showToast]);

    const handleDelete = useCallback(async () => {
        if (!productToDelete || !currentShop) return;
        const product = allProducts.find(p => p.id === productToDelete);
        if (!product || product.username !== user?.username) {
            showToast('error', 'You can only delete your own products');
            setShowDeleteModal(false);
            setProductToDelete(null);
            return;
        }
        try {
            const response = await fetch(PRODUCTS_ENDPOINT, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'delete',
                    id: productToDelete,
                    username: user.username,
                    shop_id: currentShop.shop_id
                })
            });
            const data = await response.json();
            if (data.status === 'success') {
                showToast('success', translations.deletedSuccess || 'Product deleted successfully');
                fetchProducts();
                setShowDeleteModal(false);
                setProductToDelete(null);
            } else {
                showToast('error', data.message || translations.deleteError || 'Error deleting product');
            }
        } catch (error) {
            console.error('Error deleting product:', error);
            showToast('error', translations.deleteError || 'Error deleting product');
        }
    }, [productToDelete, currentShop, allProducts, user, translations, showToast, fetchProducts]);

    const handleUpdateProduct = useCallback(async (e) => {
        e.preventDefault();
        if (!selectedProduct || !currentShop) return;
        const errors = {};
        if (!editFormData.product_name.trim()) errors.product_name = translations.productNameRequired || 'Product name is required';
        if (!editFormData.batch_no.trim()) errors.batch_no = translations.batchRequired || 'Batch number is required';
        if (!editFormData.items_per_pack || parseFloat(editFormData.items_per_pack) <= 0) errors.items_per_pack = 'Items per pack is required';
        if (!editFormData.unit_of_measure) errors.unit_of_measure = 'Unit of measure is required';
        if (!editFormData.sell_per_pack || parseFloat(editFormData.sell_per_pack) <= 0) errors.sell_per_pack = 'Retail price is required';
        if (!editFormData.total_quantity || parseFloat(editFormData.total_quantity) < 0) errors.total_quantity = 'Total quantity is required';
        if (!editFormData.quantity_alert || parseFloat(editFormData.quantity_alert) < 0) errors.quantity_alert = 'Low stock alert is required';
        if (Object.keys(errors).length > 0) { setEditFormErrors(errors); return; }
        setEditLoading(true);
        try {
            const response = await fetch(PRODUCTS_ENDPOINT, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'edit',
                    id: selectedProduct.id,
                    username: user.username,
                    shop_id: currentShop.shop_id,
                    ...editFormData
                })
            });
            const data = await response.json();
            if (data.status === 'success') {
                showToast('success', translations.productUpdated || 'Product updated successfully');
                setShowEditModal(false);
                fetchProducts();
            } else {
                showToast('error', data.message || translations.updateError || 'Error updating product');
            }
        } catch (error) {
            console.error('Error updating product:', error);
            showToast('error', translations.updateError || 'Error updating product');
        } finally {
            setEditLoading(false);
        }
    }, [selectedProduct, currentShop, editFormData, user, translations, showToast, fetchProducts]);

    const handlePageChange = useCallback((page) => { if (page >= 1 && page <= pagination.totalPages) setCurrentPage(page); }, [pagination.totalPages]);

    const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';

    // ============================================
    // RENDER HELPERS
    // ============================================

    const renderStickyHeader = () => (
        <div className="ozlistui-sticky-header">
            <div className="ozlistui-header-bg-animation">
                <div className="gradient-orb orb-1"></div>
                <div className="gradient-orb orb-2"></div>
                <div className="gradient-orb orb-3"></div>
                <div className="gradient-orb orb-4"></div>
                <div className="gradient-orb orb-5"></div>
            </div>
            <div className="ozlistui-header-content">
                <div className="ozlistui-header-left">
                    <div className="ozlistui-header-title">
                        <h1>
                           
                            {translations.productList || 'Expired Products List'}
                        </h1>
                        
                    </div>
                </div>
                <div className="ozlistui-header-right">
                    <button
                        className="ozlistui-header-filter-btn"
                        onClick={() => setShowFilters(!showFilters)}
                        title={showFilters ? "Hide Filters" : "Show Filters"}
                    >
                        <FontAwesomeIcon icon={faFilter} />
                        <span>{showFilters ? "Hide Filters" : "Filters"}</span>
                    </button>
                    {!currentShop && (
                        <button className="ozlistui-header-shop-btn" onClick={() => setShowShopSelector(true)}>
                            <FontAwesomeIcon icon={faExchangeAlt} />
                            <span>{translations.switchShop || 'Switch Shop'}</span>
                        </button>
                    )}
                </div>
            </div>
        </div>
    );

    const renderFiltersSection = () => (
        <div className="ozlistui-filters-section">
            <div className="ozlistui-filters-row">
                <CustomDropdown
                    label={translations.filterBy || 'Filter By'}
                    icon={faTag}
                    value={filterBy}
                    onChange={setFilterBy}
                    options={filterByOptions}
                    translations={translations}
                />

                {filterBy === 'productName' && (
                    <div className="ozlistui-filter-group">
                        <div className="ozlistui-floating-wrapper">
                            <input
                                type="text"
                                id="productNameFilter"
                                value={productNameFilter}
                                onChange={(e) => setProductNameFilter(e.target.value)}
                                placeholder=" "
                            />
                            <label htmlFor="productNameFilter">
                                <FontAwesomeIcon icon={faBox} />
                                {translations.productNameFilter || 'Product Name'}
                            </label>
                        </div>
                    </div>
                )}

                {filterBy === 'productCode' && (
                    <div className="ozlistui-filter-group">
                        <div className="ozlistui-floating-wrapper">
                            <input
                                type="text"
                                id="productCodeFilter"
                                value={productCodeFilter}
                                onChange={(e) => setProductCodeFilter(e.target.value)}
                                placeholder=" "
                            />
                            <label htmlFor="productCodeFilter">
                                <FontAwesomeIcon icon={faTag} />
                                {translations.productCodeFilter || 'Product Code'}
                            </label>
                        </div>
                    </div>
                )}

                {filterBy === 'batch' && (
                    <div className="ozlistui-filter-group">
                        <div className="ozlistui-floating-wrapper">
                            <input
                                type="text"
                                id="batchFilter"
                                value={batchFilter}
                                onChange={(e) => setBatchFilter(e.target.value)}
                                placeholder=" "
                            />
                            <label htmlFor="batchFilter">
                                <FontAwesomeIcon icon={faTag} />
                                {translations.batchFilter || 'Batch Number'}
                            </label>
                        </div>
                    </div>
                )}

                {filterBy === 'category' && (
                    <CustomDropdown
                        label={translations.categoryFilter || 'Category'}
                        icon={faTag}
                        value={categoryFilter}
                        onChange={setCategoryFilter}
                        options={categoryOptions}
                        translations={translations}
                    />
                )}

                {filterBy === 'company' && (
                    <div className="ozlistui-filter-group">
                        <div className="ozlistui-floating-wrapper">
                            <input
                                type="text"
                                id="companyFilter"
                                value={companyFilter}
                                onChange={(e) => setCompanyFilter(e.target.value)}
                                placeholder=" "
                            />
                            <label htmlFor="companyFilter">
                                <FontAwesomeIcon icon={faBuilding} />
                                {translations.companyFilter || 'Company'}
                            </label>
                        </div>
                    </div>
                )}

                {filterBy === 'date' && (
                    <>
                        <div className="ozlistui-filter-group ozlistui-date-filter">
                            <div className="ozlistui-floating-wrapper">
                                <input
                                    type="date"
                                    id="startDate"
                                    value={startDate}
                                    onChange={(e) => setStartDate(e.target.value)}
                                    max={new Date().toISOString().split('T')[0]}
                                    placeholder=" "
                                />
                                <label htmlFor="startDate">
                                    <FontAwesomeIcon icon={faCalendar} />
                                    {translations.startDate || 'Start Date'}
                                </label>
                            </div>
                        </div>
                        <div className="ozlistui-filter-group ozlistui-date-filter">
                            <div className="ozlistui-floating-wrapper">
                                <input
                                    type="date"
                                    id="endDate"
                                    value={endDate}
                                    onChange={(e) => setEndDate(e.target.value)}
                                    min={startDate}
                                    max={new Date().toISOString().split('T')[0]}
                                    placeholder=" "
                                />
                                <label htmlFor="endDate">
                                    <FontAwesomeIcon icon={faCalendar} />
                                    {translations.endDate || 'End Date'}
                                </label>
                            </div>
                        </div>
                    </>
                )}

                {filterBy === 'added_by' && (
                    <div className="ozlistui-filter-group">
                        <div className="ozlistui-floating-wrapper">
                            <input
                                type="text"
                                id="addedByFilter"
                                value={addedByFilter}
                                onChange={(e) => setAddedByFilter(e.target.value)}
                                placeholder=" "
                            />
                            <label htmlFor="addedByFilter">
                                <FontAwesomeIcon icon={faUser} />
                                {translations.addedByFilter || 'Added By'}
                            </label>
                        </div>
                    </div>
                )}

                <CustomDropdown
                    label="Stock Status"
                    icon={faExclamationCircle}
                    value={stockStatusFilter}
                    onChange={setStockStatusFilter}
                    options={stockStatusOptions}
                    translations={translations}
                />

                <CustomDropdown
                    label={translations.orientation || 'Orientation'}
                    icon={faFilePdf}
                    value={pdfOrientation}
                    onChange={setPdfOrientation}
                    options={pdfOrientationOptions}
                    translations={translations}
                />

                <CustomDropdown
                    label={translations.pageSize || 'Page Size'}
                    icon={faFilePdf}
                    value={pdfPageSize}
                    onChange={setPdfPageSize}
                    options={pdfPageSizeOptions}
                    translations={translations}
                />

                <div className="ozlistui-filter-group">
                    <button onClick={fetchProducts} className="ozlistui-refresh-btn" disabled={loading}>
                        <FontAwesomeIcon icon={faRefresh} spin={loading} />
                        <span className="ozlistui-btn-text">{translations.refresh || 'Refresh'}</span>
                    </button>
                </div>

                <div className="ozlistui-filter-group">
                    <button
                        onClick={exportToPDF}
                        className="ozlistui-refresh-btn"
                        disabled={filteredProducts.length === 0 || pdfLoading}
                        style={{ background: 'linear-gradient(135deg, #dc2626, #b91c1c)', color: 'white' }}
                    >
                        {pdfLoading ? <div className="ozlistui-btn-spinner-small"></div> : <FontAwesomeIcon icon={faFilePdf} />}
                        <span className="ozlistui-btn-text">{translations.exportToPDF || 'Export PDF'}</span>
                    </button>
                </div>
            </div>
        </div>
    );

    const renderTableHead = () => (
        <thead>
            <tr>
                <th className="ozlistui-id-column ozlistui-centered-cell" style={{ minWidth: '40px' }}>#</th>
                <th className="ozlistui-centered-cell" style={{ minWidth: '150px' }}>{translations.productName || 'Product Name'}</th>
                <th className="ozlistui-centered-cell" style={{ minWidth: '90px' }}>{translations.batchNumber || 'Batch'}</th>
                <th className="ozlistui-centered-cell" style={{ minWidth: '100px' }}>{translations.unit || 'Unit'}</th>
                <th className="ozlistui-centered-cell" style={{ minWidth: '100px' }}>{translations.mrp || 'MRP'}</th>
                <th className="ozlistui-centered-cell" style={{ minWidth: '100px' }}>{translations.retailPrice || 'Retail'}</th>
                <th className="ozlistui-centered-cell" style={{ minWidth: '120px' }}>{translations.expiryDate || 'Expiry Date'}</th>
                <th className="ozlistui-centered-cell" style={{ minWidth: '80px' }}>{translations.daysOverdue || 'Overdue'}</th>
                <th className="ozlistui-centered-cell" style={{ minWidth: '60px' }}>{translations.totalQuantity || 'Qty'}</th>
                
            </tr>
        </thead>
    );

    const renderTableRow = (product, index) => {
        const addedBy = product.username || '-';
        const isSelfAdded = addedBy === user?.username;
        const stockStatus = getStockStatus(
            parseFloat(product.total_quantity) || 0,
            parseFloat(product.quantity_alert) || 0
        );
        const expiryStatus = getExpiryStatus(product.expiry_date);
        const daysOverdue = getDaysOverdue(product.expiry_date);

        return (
            <tr key={product.id} className="ozlistui-expired-row" style={{ backgroundColor: '#fef2f2' }}>
                <td className="ozlistui-id-cell ozlistui-centered-cell">
                    <span className="ozlistui-cell-text">{pagination.startIndex + index + 1}</span>
                </td>
                <td className="ozlistui-centered-cell">
                    <span className="ozlistui-cell-text ozlistui-product-name">{product.product_name || 'Nil'}</span>
                </td>
                <td className="ozlistui-centered-cell">
                    <span className="ozlistui-cell-text">{product.batch_no || 'Nil'}</span>
                </td>
                <td className="ozlistui-centered-cell">
                    <span className="ozlistui-cell-text">{getUnitDisplayLabel(product.unit_of_measure) || 'Nil'}</span>
                </td>
                <td className="ozlistui-centered-cell">
                    <span className="ozlistui-cell-text">{toMoney(product.mrp)}</span>
                </td>
                <td className="ozlistui-centered-cell">
                    <span className="ozlistui-cell-text">{toMoney(product.sell_per_pack)}</span>
                </td>
                <td className="ozlistui-centered-cell">
                    <span 
                        className="ozlistui-expiry-badge ozlistui-expired"
                        style={{ 
                            backgroundColor: '#dc2626', 
                            color: '#ffffff',
                            padding: '4px 12px',
                            borderRadius: '4px',
                            fontWeight: 'bold',
                            fontSize: '12px'
                        }}
                    >
                        {formatDate(product.expiry_date)}
                    </span>
                </td>
                <td className="ozlistui-centered-cell">
                    <span style={{ color: '#dc2626', fontWeight: 'bold' }}>
                        {daysOverdue} days
                    </span>
                </td>
                <td className="ozlistui-centered-cell">
                    <span className="ozlistui-cell-text">
                        <span
                            className={`ozlistui-stock-badge ${stockStatus.label === 'Out of Stock' ? 'ozlistui-out-of-stock' : stockStatus.label === 'Low Stock' ? 'ozlistui-low-stock' : 'ozlistui-in-stock'}`}
                            style={{ backgroundColor: stockStatus.bg, color: stockStatus.color }}
                        >
                            {product.total_quantity ? Number(product.total_quantity).toFixed(0) : '0'}
                        </span>
                    </span>
                </td>
               
            </tr>
        );
    };

    const renderTable = () => (
        <div className="ozlistui-table-scroll">
            <div className="ozlistui-table-container">
                <div className="ozlistui-table-wrapper">
                    <table className="ozlistui-table">
                        {renderTableHead()}
                        <tbody>
                            {pagination.currentProducts.length === 0 ? (
                                <tr>
                                    <td colSpan={11} style={{ textAlign: 'center', padding: '2rem' }}>
                                        {fetchError
                                            ? `Error: ${fetchError}`
                                            : 'No expired products found for this shop.'}
                                    </td>
                                </tr>
                            ) : (
                                pagination.currentProducts.map((product, index) => renderTableRow(product, index))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );

    const renderPaginationControls = () => {
        if (filteredProducts.length === 0 || !currentShop) return null;
        return (
            <div className="ozlistui-pagination">
                <div className="ozlistui-pagination-info">
                    <span>{translations.showing || 'Showing'} {pagination.startIndex + 1} {translations.of || 'of'} {filteredProducts.length} {translations.products || 'products'}</span>
                </div>
                <div className="ozlistui-pagination-controls">
                    <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} className="ozlistui-pagination-btn">
                        <FontAwesomeIcon icon={faChevronLeft} />
                        <span>{translations.previous || 'Previous'}</span>
                    </button>
                    <div className="ozlistui-page-numbers">
                        {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                            let pageNum;
                            if (pagination.totalPages <= 5) {
                                pageNum = i + 1;
                            } else if (currentPage <= 3) {
                                pageNum = i + 1;
                            } else if (currentPage >= pagination.totalPages - 2) {
                                pageNum = pagination.totalPages - 4 + i;
                            } else {
                                pageNum = currentPage - 2 + i;
                            }
                            return (
                                <button key={pageNum} onClick={() => handlePageChange(pageNum)} className={`ozlistui-page-number ${currentPage === pageNum ? 'ozlistui-active' : ''}`}>
                                    {pageNum}
                                </button>
                            );
                        })}
                    </div>
                    <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === pagination.totalPages} className="ozlistui-pagination-btn">
                        <span>{translations.next || 'Next'}</span>
                        <FontAwesomeIcon icon={faChevronRight} />
                    </button>
                </div>
            </div>
        );
    };

    // ============================================
    // VIEW MODAL - Full Product Details
    // ============================================
    const renderViewModal = () => {
        if (!showViewModal || !selectedProduct) return null;
        const p = selectedProduct;
        const expiryStatus = getExpiryStatus(p.expiry_date);
        const daysOverdue = getDaysOverdue(p.expiry_date);

        return (
            <div className="ozlistui-modal-overlay" onClick={(e) => e.stopPropagation()}>
                <div className="ozlistui-modal-content ozlistui-view-modal-content" onClick={(e) => e.stopPropagation()}>
                    <div className="ozlistui-modal-header" style={{ borderBottomColor: '#dc2626' }}>
                        <div className="ozlistui-modal-icon" style={{ backgroundColor: '#dc2626' }}>
                            <FontAwesomeIcon icon={faSkull} />
                        </div>
                        <div className="ozlistui-modal-title">
                            <h3>{translations.productDetails || 'Product Details'}</h3>
                            <span className="ozlistui-product-number-badge">#{p.id}</span>
                        </div>
                        <button className="ozlistui-close-btn" onClick={() => setShowViewModal(false)}>
                            <FontAwesomeIcon icon={faTimes} />
                        </button>
                    </div>
                    <div className="ozlistui-modal-body ozlistui-view-modal-body">
                        {/* Expired Warning */}
                        <div className="ozlistui-view-section" style={{ backgroundColor: '#fef2f2', border: '2px solid #dc2626', borderRadius: '8px', padding: '15px', marginBottom: '15px' }}>
                            <h4 className="ozlistui-view-section-title" style={{ color: '#dc2626' }}>
                                <FontAwesomeIcon icon={faExclamationTriangle} /> EXPIRED PRODUCT
                            </h4>
                            <div className="ozlistui-view-grid">
                                <DetailRow label={translations.expiryDate || 'Expiry Date'} value={formatDate(p.expiry_date)} />
                                <DetailRow label={translations.daysOverdue || 'Days Overdue'} value={`${daysOverdue} days`} />
                            </div>
                        </div>

                        {/* Product Information */}
                        <div className="ozlistui-view-section">
                            <h4 className="ozlistui-view-section-title"><FontAwesomeIcon icon={faBox} />{translations.productInfo || 'Product Information'}</h4>
                            <div className="ozlistui-view-grid">
                                <DetailRow label={translations.productName || 'Product Name'} value={p.product_name} />
                                <DetailRow label={translations.batchNumber || 'Batch Number'} value={p.batch_no} />
                                <DetailRow label={translations.barcode || 'Barcode'} value={p.barcode} />
                                <DetailRow label={translations.category || 'Category'} value={getCategoryLabel(p.category)} />
                                <DetailRow label={translations.company || 'Company'} value={p.company} />
                                <DetailRow label={translations.packingType || 'Packing Type'} value={p.packing_type} />
                                <DetailRow label={translations.subPackingType || 'Sub Packing Type'} value={p.sub_packing_type} />
                                <DetailRow label={translations.itemsPerPack || 'Items Per Pack'} value={p.items_per_pack} />
                                <DetailRow label={translations.unit || 'Unit'} value={getUnitDisplayLabel(p.unit_of_measure)} />
                                <DetailRow label={translations.packContent || 'Pack Content'} value={p.pack_content_label} />
                            </div>
                        </div>

                        {/* Pricing Information */}
                        <div className="ozlistui-view-section">
                            <h4 className="ozlistui-view-section-title"><FontAwesomeIcon icon={faRupeeSign} />{translations.pricingInfo || 'Pricing Information'}</h4>
                            <div className="ozlistui-view-grid">
                                <DetailRow label={translations.mrp || 'MRP'} value={toMoney(p.mrp)} />
                                <DetailRow label={translations.retailPrice || 'Retail Price'} value={toMoney(p.sell_per_pack)} />
                                <DetailRow label={translations.mrpPerPackUnit || 'MRP per Pack Unit'} value={toMoney(p.mrp_per_pack_unit)} />
                                <DetailRow label={translations.mrpPerBaseUnit || 'MRP per Base Unit'} value={toMoney(p.mrp_per_base_unit)} />
                                <DetailRow label={translations.costPerPackUnit || 'Cost per Pack Unit'} value={toMoney(p.cost_price_per_pack_unit)} />
                                <DetailRow label={translations.costPerBaseUnit || 'Cost per Base Unit'} value={toMoney(p.cost_price_per_base_unit)} />
                                <DetailRow label={translations.sellPerItemUnit || 'Sell per Item Unit'} value={toMoney(p.sell_price_per_item_unit)} />
                                <DetailRow label={translations.sellPerBaseUnit || 'Sell per Base Unit'} value={toMoney(p.sell_price_per_base_unit)} />
                            </div>
                        </div>

                        {/* Stock Information */}
                        <div className="ozlistui-view-section">
                            <h4 className="ozlistui-view-section-title"><FontAwesomeIcon icon={faWarehouse} />{translations.stockInfo || 'Stock Information'}</h4>
                            <div className="ozlistui-view-grid">
                                <DetailRow label={translations.totalQuantity || 'Total Quantity'} value={p.total_quantity || '0'} />
                                <DetailRow label={translations.actualStock || 'Actual Stock'} value={p.actual_stock_quantity || '0'} />
                                <DetailRow label={translations.quantityAlert || 'Low Stock Alert'} value={p.quantity_alert || '0'} />
                                <DetailRow label={translations.date || 'Date'} value={formatDate(p.date)} />
                            </div>
                        </div>

                        {/* Metadata */}
                        <div className="ozlistui-view-section ozlistui-metadata-section">
                            <div className="ozlistui-view-grid">
                                <DetailRow label={translations.addedBy || 'Added By'} value={<span className="ozlistui-added-by-badge">{p.username || '-'}</span>} />
                                {p.created_at && <DetailRow label="Created At" value={formatDate(p.created_at)} />}
                                {p.updated_at && <DetailRow label="Last Updated" value={formatDate(p.updated_at)} />}
                            </div>
                        </div>
                    </div>
                    <div className="ozlistui-modal-footer">
                        <button className="ozlistui-btn ozlistui-btn-secondary" onClick={() => setShowViewModal(false)}>{translations.close || 'Close'}</button>
                        <button className="ozlistui-btn ozlistui-btn-primary" onClick={() => { setShowViewModal(false); handleEdit(p); }}>
                            <FontAwesomeIcon icon={faEdit} />
                            {translations.edit || 'Edit'}
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    // ============================================
    // EDIT MODAL
    // ============================================
    const renderEditModal = () => {
        if (!showEditModal || !selectedProduct) return null;
        return (
            <div className="ozlistui-modal-overlay" onClick={(e) => e.stopPropagation()}>
                <div
                    ref={modalRef}
                    className="ozlistui-modal-content ozlistui-edit-modal-content"
                    onClick={(e) => e.stopPropagation()}
                    style={{
                        position: 'fixed',
                        left: modalPosition.x || '50%',
                        top: modalPosition.y || '50%',
                        transform: modalPosition.x === 0 && modalPosition.y === 0 ? 'translate(-50%, -50%)' : 'none',
                        width: modalSize.width,
                        minWidth: '350px',
                        maxWidth: '95vw',
                        maxHeight: '85vh'
                    }}
                >
                    <div className="ozlistui-resize-handle-top" onMouseDown={(e) => startResize(e, 'n')}></div>
                    <div className="ozlistui-resize-handle-bottom" onMouseDown={(e) => startResize(e, 's')}></div>
                    <div className="ozlistui-resize-handle-left" onMouseDown={(e) => startResize(e, 'w')}></div>
                    <div className="ozlistui-resize-handle-right" onMouseDown={(e) => startResize(e, 'e')}></div>
                    <div className="ozlistui-resize-handle-nw" onMouseDown={(e) => startResize(e, 'nw')}></div>
                    <div className="ozlistui-resize-handle-ne" onMouseDown={(e) => startResize(e, 'ne')}></div>
                    <div className="ozlistui-resize-handle-sw" onMouseDown={(e) => startResize(e, 'sw')}></div>
                    <div className="ozlistui-resize-handle-se" onMouseDown={(e) => startResize(e, 'se')}></div>

                    <div className="ozlistui-modal-header" onMouseDown={startDrag}>
                        <div className="ozlistui-modal-icon"><FontAwesomeIcon icon={faEdit} /></div>
                        <div className="ozlistui-modal-title">
                            <h3>{translations.editProduct || 'Edit Product'}</h3>
                            <span className="ozlistui-product-number-badge">#{selectedProduct.id}</span>
                        </div>
                        <div className="ozlistui-width-control-group">
                            {Object.entries(widthModes).map(([key, mode]) => (
                                <button key={key} className={`ozlistui-width-btn ${currentWidthMode === key ? 'ozlistui-active' : ''}`} onClick={() => setWidthMode(key)} title={mode.label}>
                                    <FontAwesomeIcon icon={mode.icon} />
                                </button>
                            ))}
                        </div>
                        <button className="ozlistui-close-btn" onClick={() => setShowEditModal(false)}>
                            <FontAwesomeIcon icon={faTimes} />
                        </button>
                    </div>

                    <form onSubmit={handleUpdateProduct}>
                        <div className="ozlistui-modal-body">
                            <div className="ozlistui-form-grid">
                                {/* Product Info */}
                                <div className={`ozlistui-form-group ${editFormErrors.product_name ? 'ozlistui-has-error' : ''}`}>
                                    <div className="ozlistui-input-wrapper">
                                        <input
                                            type="text"
                                            id="edit_product_name"
                                            name="product_name"
                                            value={editFormData.product_name}
                                            onChange={(e) => setEditFormData({ ...editFormData, product_name: e.target.value })}
                                            placeholder=" "
                                            className={editFormErrors.product_name ? 'ozlistui-error' : ''}
                                        />
                                        <label htmlFor="edit_product_name">
                                            <FontAwesomeIcon icon={faBox} />
                                            {translations.productName || 'Product Name'} <span className="ozlistui-required">*</span>
                                        </label>
                                    </div>
                                    {editFormErrors.product_name && (
                                        <div className="ozlistui-error-message">
                                            <FontAwesomeIcon icon={faExclamationTriangle} />
                                            {editFormErrors.product_name}
                                        </div>
                                    )}
                                </div>

                                <div className={`ozlistui-form-group ${editFormErrors.batch_no ? 'ozlistui-has-error' : ''}`}>
                                    <div className="ozlistui-input-wrapper">
                                        <input
                                            type="text"
                                            id="edit_batch_no"
                                            name="batch_no"
                                            value={editFormData.batch_no}
                                            onChange={(e) => setEditFormData({ ...editFormData, batch_no: e.target.value })}
                                            placeholder=" "
                                            className={editFormErrors.batch_no ? 'ozlistui-error' : ''}
                                        />
                                        <label htmlFor="edit_batch_no">
                                            <FontAwesomeIcon icon={faTag} />
                                            {translations.batchNumber || 'Batch Number'} <span className="ozlistui-required">*</span>
                                        </label>
                                    </div>
                                    {editFormErrors.batch_no && (
                                        <div className="ozlistui-error-message">
                                            <FontAwesomeIcon icon={faExclamationTriangle} />
                                            {editFormErrors.batch_no}
                                        </div>
                                    )}
                                </div>

                                <div className="ozlistui-form-group">
                                    <div className="ozlistui-input-wrapper">
                                        <input
                                            type="text"
                                            id="edit_product_code"
                                            name="product_code"
                                            value={editFormData.product_code}
                                            onChange={(e) => setEditFormData({ ...editFormData, product_code: e.target.value })}
                                            placeholder=" "
                                        />
                                        <label htmlFor="edit_product_code">
                                            <FontAwesomeIcon icon={faTag} />
                                            {translations.productCode || 'Product Code'}
                                        </label>
                                    </div>
                                </div>

                                <div className="ozlistui-form-group">
                                    <div className="ozlistui-input-wrapper">
                                        <input
                                            type="text"
                                            id="edit_company"
                                            name="company"
                                            value={editFormData.company}
                                            onChange={(e) => setEditFormData({ ...editFormData, company: e.target.value })}
                                            placeholder=" "
                                        />
                                        <label htmlFor="edit_company">
                                            <FontAwesomeIcon icon={faBuilding} />
                                            {translations.company || 'Company'}
                                        </label>
                                    </div>
                                </div>

                                <div className="ozlistui-form-group">
                                    <div className="ozlistui-input-wrapper">
                                        <select
                                            id="edit_category"
                                            name="category"
                                            value={editFormData.category}
                                            onChange={(e) => setEditFormData({ ...editFormData, category: e.target.value })}
                                        >
                                            <option value="">{translations.selectCategory || 'Select Category'}</option>
                                            {categoryOptions.filter(opt => opt.value).map(option => (
                                                <option key={option.value} value={option.value}>{option.label}</option>
                                            ))}
                                        </select>
                                        <label htmlFor="edit_category">
                                            <FontAwesomeIcon icon={faTag} />
                                            {translations.category || 'Category'}
                                        </label>
                                    </div>
                                </div>

                                {/* Packing */}
                                <div className="ozlistui-form-group">
                                    <div className="ozlistui-input-wrapper">
                                        <input
                                            type="text"
                                            id="edit_packing_type"
                                            name="packing_type"
                                            value={editFormData.packing_type}
                                            onChange={(e) => setEditFormData({ ...editFormData, packing_type: e.target.value })}
                                            placeholder=" "
                                        />
                                        <label htmlFor="edit_packing_type">
                                            <FontAwesomeIcon icon={faBoxes} />
                                            {translations.packingType || 'Packing Type'}
                                        </label>
                                    </div>
                                </div>

                                <div className={`ozlistui-form-group ${editFormErrors.items_per_pack ? 'ozlistui-has-error' : ''}`}>
                                    <div className="ozlistui-input-wrapper">
                                        <input
                                            type="number"
                                            id="edit_items_per_pack"
                                            name="items_per_pack"
                                            value={editFormData.items_per_pack}
                                            onChange={(e) => setEditFormData({ ...editFormData, items_per_pack: e.target.value })}
                                            step="1"
                                            min="0"
                                            placeholder=" "
                                            className={editFormErrors.items_per_pack ? 'ozlistui-error' : ''}
                                        />
                                        <label htmlFor="edit_items_per_pack">
                                            <FontAwesomeIcon icon={faCalculator} />
                                            {translations.itemsPerPack || 'Items Per Pack'} <span className="ozlistui-required">*</span>
                                        </label>
                                    </div>
                                    {editFormErrors.items_per_pack && (
                                        <div className="ozlistui-error-message">
                                            <FontAwesomeIcon icon={faExclamationTriangle} />
                                            {editFormErrors.items_per_pack}
                                        </div>
                                    )}
                                </div>

                                <div className={`ozlistui-form-group ${editFormErrors.unit_of_measure ? 'ozlistui-has-error' : ''}`}>
                                    <div className="ozlistui-input-wrapper">
                                        <input
                                            type="text"
                                            id="edit_unit_of_measure"
                                            name="unit_of_measure"
                                            value={editFormData.unit_of_measure}
                                            onChange={(e) => setEditFormData({ ...editFormData, unit_of_measure: e.target.value })}
                                            placeholder=" "
                                            className={editFormErrors.unit_of_measure ? 'ozlistui-error' : ''}
                                        />
                                        <label htmlFor="edit_unit_of_measure">
                                            <FontAwesomeIcon icon={faBalanceScale} />
                                            {translations.unit || 'Unit'} <span className="ozlistui-required">*</span>
                                        </label>
                                    </div>
                                    {editFormErrors.unit_of_measure && (
                                        <div className="ozlistui-error-message">
                                            <FontAwesomeIcon icon={faExclamationTriangle} />
                                            {editFormErrors.unit_of_measure}
                                        </div>
                                    )}
                                </div>

                                {/* Pricing */}
                                <div className={`ozlistui-form-group ${editFormErrors.sell_per_pack ? 'ozlistui-has-error' : ''}`}>
                                    <div className="ozlistui-input-wrapper">
                                        <input
                                            type="number"
                                            id="edit_sell_per_pack"
                                            name="sell_per_pack"
                                            value={editFormData.sell_per_pack}
                                            onChange={(e) => setEditFormData({ ...editFormData, sell_per_pack: e.target.value })}
                                            step="0.01"
                                            min="0"
                                            placeholder=" "
                                            className={editFormErrors.sell_per_pack ? 'ozlistui-error' : ''}
                                        />
                                        <label htmlFor="edit_sell_per_pack">
                                            <FontAwesomeIcon icon={faRupeeSign} />
                                            {translations.retailPrice || 'Retail Price'} <span className="ozlistui-required">*</span>
                                        </label>
                                    </div>
                                    {editFormErrors.sell_per_pack && (
                                        <div className="ozlistui-error-message">
                                            <FontAwesomeIcon icon={faExclamationTriangle} />
                                            {editFormErrors.sell_per_pack}
                                        </div>
                                    )}
                                </div>

                                <div className="ozlistui-form-group">
                                    <div className="ozlistui-input-wrapper">
                                        <input
                                            type="number"
                                            id="edit_mrp"
                                            name="mrp"
                                            value={editFormData.mrp}
                                            onChange={(e) => setEditFormData({ ...editFormData, mrp: e.target.value })}
                                            step="0.01"
                                            min="0"
                                            placeholder=" "
                                        />
                                        <label htmlFor="edit_mrp">
                                            <FontAwesomeIcon icon={faMoneyBillWave} />
                                            {translations.mrp || 'MRP'}
                                        </label>
                                    </div>
                                </div>

                                {/* Stock */}
                                <div className={`ozlistui-form-group ${editFormErrors.total_quantity ? 'ozlistui-has-error' : ''}`}>
                                    <div className="ozlistui-input-wrapper">
                                        <input
                                            type="number"
                                            id="edit_total_quantity"
                                            name="total_quantity"
                                            value={editFormData.total_quantity}
                                            onChange={(e) => setEditFormData({ ...editFormData, total_quantity: e.target.value })}
                                            step="0.001"
                                            min="0"
                                            placeholder=" "
                                            className={editFormErrors.total_quantity ? 'ozlistui-error' : ''}
                                        />
                                        <label htmlFor="edit_total_quantity">
                                            <FontAwesomeIcon icon={faWarehouse} />
                                            {translations.totalQuantity || 'Total Quantity'} <span className="ozlistui-required">*</span>
                                        </label>
                                    </div>
                                    {editFormErrors.total_quantity && (
                                        <div className="ozlistui-error-message">
                                            <FontAwesomeIcon icon={faExclamationTriangle} />
                                            {editFormErrors.total_quantity}
                                        </div>
                                    )}
                                </div>

                                <div className={`ozlistui-form-group ${editFormErrors.quantity_alert ? 'ozlistui-has-error' : ''}`}>
                                    <div className="ozlistui-input-wrapper">
                                        <input
                                            type="number"
                                            id="edit_quantity_alert"
                                            name="quantity_alert"
                                            value={editFormData.quantity_alert}
                                            onChange={(e) => setEditFormData({ ...editFormData, quantity_alert: e.target.value })}
                                            step="0.001"
                                            min="0"
                                            placeholder=" "
                                            className={editFormErrors.quantity_alert ? 'ozlistui-error' : ''}
                                        />
                                        <label htmlFor="edit_quantity_alert">
                                            <FontAwesomeIcon icon={faExclamationTriangle} />
                                            {translations.quantityAlert || 'Low Stock Alert'} <span className="ozlistui-required">*</span>
                                        </label>
                                    </div>
                                    {editFormErrors.quantity_alert && (
                                        <div className="ozlistui-error-message">
                                            <FontAwesomeIcon icon={faExclamationTriangle} />
                                            {editFormErrors.quantity_alert}
                                        </div>
                                    )}
                                </div>

                                {/* Expiry Date - Highlighted in RED */}
                                <div className={`ozlistui-form-group ${editFormErrors.expiry_date ? 'ozlistui-has-error' : ''}`} style={{ border: '2px solid #dc2626', borderRadius: '6px', padding: '5px' }}>
                                    <div className="ozlistui-input-wrapper">
                                        <input
                                            type="date"
                                            id="edit_expiry_date"
                                            name="expiry_date"
                                            value={editFormData.expiry_date}
                                            onChange={(e) => setEditFormData({ ...editFormData, expiry_date: e.target.value })}
                                            placeholder=" "
                                            style={{ borderColor: '#dc2626' }}
                                        />
                                        <label htmlFor="edit_expiry_date" style={{ color: '#dc2626' }}>
                                            <FontAwesomeIcon icon={faCalendarAlt} style={{ color: '#dc2626' }} />
                                            {translations.expiryDate || 'Expiry Date'} <span className="ozlistui-required" style={{ color: '#dc2626' }}>*</span>
                                        </label>
                                    </div>
                                    <div style={{ fontSize: '11px', color: '#dc2626', marginTop: '2px', paddingLeft: '10px' }}>
                                        <FontAwesomeIcon icon={faExclamationTriangle} /> This product is EXPIRED
                                    </div>
                                    {editFormErrors.expiry_date && (
                                        <div className="ozlistui-error-message">
                                            <FontAwesomeIcon icon={faExclamationTriangle} />
                                            {editFormErrors.expiry_date}
                                        </div>
                                    )}
                                </div>

                                <div className="ozlistui-form-group">
                                    <div className="ozlistui-input-wrapper">
                                        <input
                                            type="date"
                                            id="edit_date"
                                            name="date"
                                            value={editFormData.date}
                                            onChange={(e) => setEditFormData({ ...editFormData, date: e.target.value })}
                                            max={new Date().toISOString().split('T')[0]}
                                            placeholder=" "
                                        />
                                        <label htmlFor="edit_date">
                                            <FontAwesomeIcon icon={faCalendar} />
                                            {translations.date || 'Date'}
                                        </label>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="ozlistui-modal-footer">
                            <button type="button" className="ozlistui-btn ozlistui-btn-secondary" onClick={() => setShowEditModal(false)}>
                                <FontAwesomeIcon icon={faTimes} />
                                {translations.cancel || 'Cancel'}
                            </button>
                            <button type="submit" className="ozlistui-btn ozlistui-btn-primary" disabled={editLoading}>
                                {editLoading ? (
                                    <>
                                        <div className="ozlistui-btn-spinner"></div>
                                        <span>{translations.updating || 'Updating...'}</span>
                                    </>
                                ) : (
                                    <>
                                        <FontAwesomeIcon icon={faSave} />
                                        <span>{translations.saveChanges || 'Save Changes'}</span>
                                    </>
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        );
    };

    // ============================================
    // DELETE MODAL
    // ============================================
    const renderDeleteModal = () => {
        if (!showDeleteModal) return null;
        return (
            <div className="ozlistui-modal-overlay" onClick={(e) => e.stopPropagation()}>
                <div className="ozlistui-modal-content ozlistui-delete-modal" onClick={(e) => e.stopPropagation()}>
                    <div className="ozlistui-modal-header ozlistui-delete-header">
                        <div className="ozlistui-modal-icon"><FontAwesomeIcon icon={faExclamationTriangle} /></div>
                        <div className="ozlistui-modal-title"><h3>{translations.confirmDelete || 'Confirm Delete'}</h3></div>
                        <button className="ozlistui-close-btn" onClick={() => setShowDeleteModal(false)}><FontAwesomeIcon icon={faTimes} /></button>
                    </div>
                    <div className="ozlistui-modal-body">
                        <div className="ozlistui-delete-message">
                            <p>{translations.deleteMessage || 'Are you sure you want to delete this product?'}</p>
                            <p><FontAwesomeIcon icon={faExclamationTriangle} /> {translations.deleteWarning || 'This action cannot be undone.'}</p>
                        </div>
                    </div>
                    <div className="ozlistui-modal-footer">
                        <button type="button" className="ozlistui-btn ozlistui-btn-secondary" onClick={() => setShowDeleteModal(false)}><FontAwesomeIcon icon={faTimes} />{translations.noCancel || 'No, Cancel'}</button>
                        <button type="button" className="ozlistui-btn ozlistui-btn-delete" onClick={handleDelete}><FontAwesomeIcon icon={faTrash} />{translations.yesDelete || 'Yes, Delete'}</button>
                    </div>
                </div>
            </div>
        );
    };

    // ============================================
    // MAIN RENDER
    // ============================================
    return (
        <div className={`ozlistui-container ${isRTL ? 'ozlistui-rtl' : 'ozlistui-ltr'} ${darkMode ? 'ozlistui-dark' : ''}`}>
            <ToastNotification toast={toast} setToast={setToast} translations={translations} />

            <ShopSelectorModal
                isOpen={showShopSelector}
                onClose={() => setShowShopSelector(false)}
                shops={availableShops}
                currentShop={currentShop}
                onSelectShop={handleSwitchShop}
                translations={translations}
                loading={loadingShops}
            />

            {renderStickyHeader()}
            {showFilters && renderFiltersSection()}

            {loading ? (
                <div className="ozlistui-loading-container">
                    <div className="ozlistui-loading-spinner"></div>
                    <p>{translations.loadingUserData || 'Loading expired products...'}</p>
                </div>
            ) : !currentShop ? (
                <div className="ozlistui-no-shop-container">
                    <div className="ozlistui-no-shop-message">
                        <FontAwesomeIcon icon={faStore} />
                        <h3>{translations.noShopSelected || 'No Shop Selected'}</h3>
                        <p>{translations.selectShop || 'Please select a shop to view expired products'}</p>
                        <button className="ozlistui-btn ozlistui-btn-primary" onClick={() => setShowShopSelector(true)}>
                            <FontAwesomeIcon icon={faExchangeAlt} />
                            <span>{translations.switchShop || 'Switch Shop'}</span>
                        </button>
                    </div>
                </div>
            ) : (
                <>
                    {renderTable()}
                    {renderPaginationControls()}
                </>
            )}

            {renderViewModal()}
            {renderEditModal()}
            {renderDeleteModal()}
        </div>
    );
};

export default PharmacyExpiredProducts;